import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "motion/react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import {
  Play,
  Zap,
  Lock,
  Eye,
  Network,
  Activity,
  ArrowRight,
  CheckCircle2,
  Star,
  Menu,
  X,
  LineChart,
  LayoutDashboard,
  Calendar,
  BookOpen,
  Bot,
  Twitter,
  Instagram,
  Linkedin,
  Users,
  BarChart2,
  Wallet,
  MapPin,
  Crown,
  Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useLenis } from '../context/LenisContext';

import Navbar from "../components/landing/Navbar";
import AboutUs from "../components/landing/AboutUs";
import Contact from "../components/landing/Contact";
import Privacy from "../components/landing/Privacy";
import Terms from "../components/landing/Terms";
import Footer from "../components/landing/Footer";
import ApiSyncModal from "../components/ApiSyncModal";
import logoAsset from "../assets/logo-asset.png";
import shieldLandingAsset from "../assets/shield-landing.png";
import contactAsset from "../assets/contact1.png";
import dashboardAsset from "../assets/dashboard-asset.webp";
import reportsAsset from "../assets/reports-asset.webp";
import calendarAsset from "../assets/calendar-asset.webp";
import tradesAsset from "../assets/trades-asset.webp";
import notebookAsset from "../assets/notebook-asset.webp";
import aiAsset from "../assets/ai-assistant.webp";
import bitgetLogo from "../assets/bitget.jpg";
import indodaxLogo from "../assets/indodax.jpg";
import bybitLogo from "../assets/bybit.jpg";

// Wallet & DEX Logos
import dexScreenerLogo from "../assets/dex-screener-logo.jpg";
import coinbaseLogo from "../assets/coinbase-logo.png";
import metamaskLogo from "../assets/metamask-logo.png";
import okxWalletLogo from "../assets/okx-wallet-logo.png";
import phantomLogo from "../assets/phantom-logo.png";
import solflareLogo from "../assets/solflare-logo.png";
import trustWalletLogo from "../assets/trust-wallet-logo.png";

gsap.registerPlugin(useGSAP, ScrollTrigger, ScrollToPlugin);

// ============================================
// 🎬 DEMO VIDEO CONFIGURATION
// ============================================
// TODO: Ganti URL ini dengan YouTube video embed URL setelah video ready
// Format: "https://www.youtube.com/embed/YOUR_VIDEO_ID"
// Cara ambil: YouTube → Share → Embed → Copy URL dari src="..."
const DEMO_VIDEO_URL = ""; // Kosong = show "Coming Soon" message

type BrokerKey = 'binance' | 'okx' | 'indodax' | 'mexc' | 'bitget' | 'tokocrypto' | 'bybit';
type WalletKey = 'dexscreener' | 'coinbase' | 'metamask' | 'okxwallet' | 'phantom' | 'solflare' | 'trustwallet';

const brokerInfo: Record<BrokerKey, { name: string; description: string; website: string; color: string; glow: string }> = {
  binance: {
    name: 'Binance',
    description: "World's largest crypto exchange by volume",
    website: 'binance.com',
    color: '#F0B90B',
    glow: '#F0B90B',
  },
  mexc: {
    name: 'MEXC',
    description: 'Global crypto exchange with 1,500+ trading pairs',
    website: 'mexc.com',
    color: '#2354E6',
    glow: '#2354E6',
  },
  okx: {
    name: 'OKX',
    description: 'Leading crypto exchange & Web3 ecosystem',
    website: 'okx.com',
    color: '#F93827',
    glow: '#F93827',
  },
  bitget: {
    name: 'Bitget',
    description: 'Leading crypto copy trading & futures exchange',
    website: 'bitget.com',
    color: '#00F0FF',
    glow: '#00F0FF',
  },
  indodax: {
    name: 'Indodax',
    description: "Indonesia's leading regulated crypto exchange",
    website: 'indodax.com',
    color: '#1B2C68',
    glow: '#1B2C68',
  },
  tokocrypto: {
    name: 'Tokocrypto',
    description: "Indonesia's leading regulated crypto exchange",
    website: 'tokocrypto.com',
    color: '#DA251C',
    glow: '#DA251C',
  },
  bybit: {
    name: 'Bybit',
    description: "Leading global derivatives & spot trading CEX",
    website: 'bybit.com',
    color: '#F7A600',
    glow: '#F7A600',
  },
};

const walletInfo: Record<WalletKey, { name: string; description: string; website: string; color: string; glow: string }> = {
  dexscreener: {
    name: 'DexScreener',
    description: 'Real-time DEX analytics and token discovery',
    website: 'dexscreener.com',
    color: '#0FBA83',
    glow: '#0FBA83',
  },
  coinbase: {
    name: 'Coinbase Wallet',
    description: 'Self-custody wallet for Web3',
    website: 'wallet.coinbase.com',
    color: '#0052FF',
    glow: '#0052FF',
  },
  metamask: {
    name: 'MetaMask',
    description: 'The leading Web3 wallet for Ethereum',
    website: 'metamask.io',
    color: '#F6851B',
    glow: '#F6851B',
  },
  okxwallet: {
    name: 'OKX Wallet',
    description: 'Multi-chain Web3 wallet',
    website: 'okx.com/web3',
    color: '#000000',
    glow: '#FFFFFF',
  },
  phantom: {
    name: 'Phantom',
    description: 'The friendly Solana wallet',
    website: 'phantom.app',
    color: '#AB9FF2',
    glow: '#AB9FF2',
  },
  solflare: {
    name: 'Solflare',
    description: 'Solana wallet built for DeFi',
    website: 'solflare.com',
    color: '#FC6C2C',
    glow: '#FC6C2C',
  },
  trustwallet: {
    name: 'Trust Wallet',
    description: 'The most trusted crypto wallet',
    website: 'trustwallet.com',
    color: '#3375BB',
    glow: '#3375BB',
  },
};

const brokerConfigs: Record<BrokerKey, {
  icon: string;
  alt: string;
  duration: number;
}> = {
  binance: {
    icon: 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/270.png',
    alt: 'Binance',
    duration: 40,
  },
  okx: {
    icon: 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/294.png',
    alt: 'OKX',
    duration: 40,
  },
  indodax: {
    icon: indodaxLogo,
    alt: 'Indodax',
    duration: 40,
  },
  mexc: {
    icon: 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/544.png',
    alt: 'MEXC',
    duration: 40,
  },
  bitget: {
    icon: bitgetLogo,
    alt: 'Bitget',
    duration: 40,
  },
  tokocrypto: {
    icon: 'https://s2.coinmarketcap.com/static/img/exchanges/64x64/525.png',
    alt: 'Tokocrypto',
    duration: 40,
  },
  bybit: {
    icon: bybitLogo,
    alt: 'Bybit',
    duration: 40,
  },
};

const walletConfigs: Record<WalletKey, {
  icon: string;
  alt: string;
  duration: number;
}> = {
  dexscreener: {
    icon: dexScreenerLogo,
    alt: 'DexScreener',
    duration: 40,
  },
  coinbase: {
    icon: coinbaseLogo,
    alt: 'Coinbase Wallet',
    duration: 40,
  },
  metamask: {
    icon: metamaskLogo,
    alt: 'MetaMask',
    duration: 40,
  },
  okxwallet: {
    icon: okxWalletLogo,
    alt: 'OKX Wallet',
    duration: 40,
  },
  phantom: {
    icon: phantomLogo,
    alt: 'Phantom',
    duration: 40,
  },
  solflare: {
    icon: solflareLogo,
    alt: 'Solflare',
    duration: 40,
  },
  trustwallet: {
    icon: trustWalletLogo,
    alt: 'Trust Wallet',
    duration: 40,
  },
};


const HERO_TYPE_MS = 75;
const HERO_DELETE_MS = 50;
const HERO_WORD_GAP_MS = 80;
const HERO_DELETE_GAP_MS = 60;
const HERO_PAUSE_AFTER_TYPE_MS = 3000;
const HERO_PAUSE_AFTER_DELETE_MS = 800;
const TESTIMONIALS_TYPE_MS = 45;
const TESTIMONIALS_WORD_GAP_MS = 80;
const PARTNER_TYPE_MS = 45;
const PARTNER_WORD_GAP_MS = 80;
const PARTNER_CARD_FLOAT_Y = -14;
const PARTNER_CARD_FLOAT_DURATION = 7;
const PARTNER_BADGE_FLOAT_Y = -8;
const PARTNER_BADGE_FLOAT_DURATION = 5.5;

const typeText = (
  el: HTMLElement,
  text: string,
  intervalMs: number,
  onComplete: () => void
): (() => void) => {
  let index = 0;
  el.textContent = "";
  const id = setInterval(() => {
    index++;
    el.textContent = text.slice(0, index);
    if (index >= text.length) {
      clearInterval(id);
      onComplete();
    }
  }, intervalMs);
  return () => clearInterval(id);
};

const deleteText = (
  el: HTMLElement,
  intervalMs: number,
  onComplete: () => void
): (() => void) => {
  let len = el.textContent?.length ?? 0;
  const id = setInterval(() => {
    len--;
    el.textContent = (el.textContent ?? "").slice(0, len);
    if (len <= 0) {
      clearInterval(id);
      onComplete();
    }
  }, intervalMs);
  return () => clearInterval(id);
};

import { useSEO } from '../hooks/useSEO';

export default function LandingPage() {
  const [currentPage, setCurrentPage] = useState('home');

  // Dynamic SEO configuration based on active page
  const seoConfig = React.useMemo(() => {
    const canonicalBase = 'https://catatcrypto.com';
    switch (currentPage) {
      case 'about':
        return {
          title: 'Tentang Kami & Tim CatatCrypto',
          description: 'Pelajari kisah di balik CatatCrypto, visi misi kami untuk mendemokrasikan jurnal trading crypto profesional, dan tim yang membangun platform ini.',
          keywords: 'about us, catatcrypto team, trading journal origin, crypto analytics background',
          canonical: `${canonicalBase}/about`,
          ogUrl: `${canonicalBase}/about`,
        };
      case 'contact':
        return {
          title: 'Hubungi Customer Support Kami',
          description: 'Butuh bantuan dengan integrasi API CEX atau sinkronisasi wallet? Hubungi tim support CatatCrypto. Kami siap membantu 24/7.',
          keywords: 'contact support, customer help, contact email, catatcrypto response team',
          canonical: `${canonicalBase}/contact`,
          ogUrl: `${canonicalBase}/contact`,
        };
      case 'privacy':
        return {
          title: 'Kebijakan Privasi & Keamanan Data',
          description: 'Kami berkomitmen melindungi privasi data trading Anda. Informasi API keys dan wallet address dienkripsi dengan standar keamanan tinggi.',
          keywords: 'privacy policy, API key encryption, data protection, security standard',
          canonical: `${canonicalBase}/privacy`,
          ogUrl: `${canonicalBase}/privacy`,
        };
      case 'terms':
        return {
          title: 'Syarat & Ketentuan Layanan Pengguna',
          description: 'Baca peraturan penggunaan platform CatatCrypto, lisensi trading journal, dan batasan tanggung jawab penyedia layanan.',
          keywords: 'terms of service, user agreement, billing conditions, refund policy',
          canonical: `${canonicalBase}/terms`,
          ogUrl: `${canonicalBase}/terms`,
        };
      case 'home':
      default:
        return {
          title: 'Professional Crypto Trade Journal & Analytics',
          description: 'Track, analyze, and optimize your cryptocurrency trades. Auto-sync with Indodax, Binance, OKX. Real-time PnL tracking and AI-powered trading analytics.',
          keywords: 'crypto journal, trading journal, trade tracker, indodax sync, binance sync, crypto portfolio tracker, catat crypto',
          canonical: canonicalBase + '/',
          ogUrl: canonicalBase + '/',
          structuredData: {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CatatCrypto",
            "operatingSystem": "Web",
            "applicationCategory": "FinanceApplication",
            "description": "Professional cryptocurrency trading journal and analytics dashboard with automatic API sync.",
            "offers": {
              "@type": "Offer",
              "price": "0.00",
              "priceCurrency": "USD"
            }
          }
        };
    }
  }, [currentPage]);

  useSEO(seoConfig);

  const { t, i18n } = useTranslation();
  const isId = i18n.language === 'id';
  const navigate = useNavigate();
  const location = useLocation();
  const lenisRef = useLenis();

  const handleSetPage = (page: string) => {
    if (page === 'home') {
      navigate('/');
    } else {
      navigate('/' + page);
    }
  };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeBroker, setActiveBroker] = useState<BrokerKey | null>(null);
  const [hoveredBroker, setHoveredBroker] = useState<BrokerKey | null>(null);
  const [activeWallet, setActiveWallet] = useState<WalletKey | null>(null);
  const [hoveredWallet, setHoveredWallet] = useState<WalletKey | null>(null);
  
  // Combine all icons in one orbit
  type OrbitIconKey = BrokerKey | WalletKey;
  const allOrbitKeys: OrbitIconKey[] = [
    'binance', 'metamask', 'okx', 'phantom', 'indodax', 'coinbase', 'mexc',
    'trustwallet', 'bitget', 'dexscreener', 'tokocrypto', 'solflare', 'bybit', 'okxwallet'
  ];
  
  const brokerKeys: BrokerKey[] = ['binance', 'okx', 'indodax', 'mexc', 'bitget', 'tokocrypto', 'bybit'];
  const walletKeys: WalletKey[] = ['dexscreener', 'coinbase', 'metamask', 'okxwallet', 'phantom', 'solflare', 'trustwallet'];
  const brokerRefs = useRef<Record<BrokerKey, React.RefObject<HTMLDivElement | null>>>({
    binance: React.createRef<HTMLDivElement | null>(),
    okx: React.createRef<HTMLDivElement | null>(),
    indodax: React.createRef<HTMLDivElement | null>(),
    mexc: React.createRef<HTMLDivElement | null>(),
    bitget: React.createRef<HTMLDivElement | null>(),
    tokocrypto: React.createRef<HTMLDivElement | null>(),
    bybit: React.createRef<HTMLDivElement | null>(),
  }).current;
  const walletRefs = useRef<Record<WalletKey, React.RefObject<HTMLDivElement | null>>>({
    dexscreener: React.createRef<HTMLDivElement | null>(),
    coinbase: React.createRef<HTMLDivElement | null>(),
    metamask: React.createRef<HTMLDivElement | null>(),
    okxwallet: React.createRef<HTMLDivElement | null>(),
    phantom: React.createRef<HTMLDivElement | null>(),
    solflare: React.createRef<HTMLDivElement | null>(),
    trustwallet: React.createRef<HTMLDivElement | null>(),
  }).current;
  const heroSectionRef = useRef<HTMLElement | null>(null);
  const heroContentRef = useRef<HTMLDivElement | null>(null);
  const meshBlob1Ref = useRef<HTMLDivElement | null>(null);
  const meshBlob2Ref = useRef<HTMLDivElement | null>(null);
  const meshBlob3Ref = useRef<HTMLDivElement | null>(null);
  const heroRingRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [pricingCycle, setPricingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isApiSyncModalOpen, setIsApiSyncModalOpen] = useState(false);
  const brokerStageRef = useRef<HTMLDivElement | null>(null);
  const featuresSectionRef = useRef<HTMLDivElement>(null);
  const statsBarRef = useRef<HTMLDivElement | null>(null);
  const tradersNumberRef = useRef<HTMLSpanElement | null>(null);
  const volumeNumberRef = useRef<HTMLSpanElement | null>(null);
  const compSectionRef = useRef<HTMLElement | null>(null);
  const compLeftBgRef = useRef<HTMLDivElement | null>(null);
  const compRightBgRef = useRef<HTMLDivElement | null>(null);
  const comp42Ref = useRef<HTMLSpanElement | null>(null);
  const comp89Ref = useRef<HTMLSpanElement | null>(null);
  const testimonialsSectionRef = useRef<HTMLElement | null>(null);
  const testimonialsGridRef = useRef<HTMLDivElement | null>(null);
  const testimonialsEntranceDoneRef = useRef(false);
  const partnerSectionRef = useRef<HTMLElement | null>(null);
  const partnerCardRef = useRef<HTMLDivElement | null>(null);
  const partnerBadgeRef = useRef<HTMLDivElement | null>(null);
  const [activeFeature, setActiveFeature] = useState(0);
  const [heroAnimKey, setHeroAnimKey] = useState(0);
  const [brokerAnimKey, setBrokerAnimKey] = useState(0);
  const heroAnimReadyRef = useRef(false);
  const [activeTab, setActiveTab] = useState(0);
  const [orbitGlowActive, setOrbitGlowActive] = useState(false);
  const mockupSectionRef = useRef<HTMLElement | null>(null);
  const mockupTlRef = useRef<gsap.core.Timeline | null>(null);
  const imgRefs = useRef<(HTMLImageElement | null)[]>([]);
  const frameContentRef = useRef<HTMLDivElement | null>(null);
  const ctaSectionRef = useRef<HTMLElement | null>(null);
  const ctaHeadlineRef = useRef<HTMLHeadingElement | null>(null);
  const ctaHasRunRef = useRef(false);
  const ctaIsTypingRef = useRef(false);
  const ctaPendingLangRef = useRef<string | null>(null);
  const heroTitle = t('hero.title');
  const prevHeroTitleRef = React.useRef(heroTitle);
  const pendingTitleRef = React.useRef<string | null>(null);
  const currentActiveTitleRef = React.useRef(heroTitle);

  useEffect(() => {
    if (heroTitle !== prevHeroTitleRef.current) {
      pendingTitleRef.current = heroTitle;
      prevHeroTitleRef.current = heroTitle;
    }
    currentActiveTitleRef.current = heroTitle;
  }, [heroTitle]);
  const panels = [
    {
      id: 0,
      number: '01',
      title: t('bento.master'),
      description: t('bento.masterDesc'),
      visual: 'chart',
    },
    {
      id: 1,
      number: '02',
      title: t('bento.community'),
      description: t('bento.communityDesc'),
      visual: 'network',
    },
    {
      id: 2,
      number: '03',
      title: t('bento.accurate'),
      description: t('bento.accurateDesc'),
      visual: 'zap',
    },
    {
      id: 3,
      number: '04',
      title: t('bento.security'),
      description: t('bento.securityDesc'),
      visual: 'shield',
    },
  ];
  const mockupTabs = [
    { icon: LayoutDashboard, name: t('appMockup.tabs.dashboard'), slug: 'dashboard', asset: dashboardAsset, needsScroll: true },
    { icon: LineChart, name: t('appMockup.tabs.reports'), slug: 'reports', asset: reportsAsset, needsScroll: true },
    { icon: Calendar, name: t('appMockup.tabs.calendar'), slug: 'calendar', asset: calendarAsset, needsScroll: false },
    { icon: Activity, name: t('appMockup.tabs.trades'), slug: 'trades', asset: tradesAsset, needsScroll: false },
    { icon: BookOpen, name: t('appMockup.tabs.notebook'), slug: 'notebook', asset: notebookAsset, needsScroll: true },
    { icon: Bot, name: t('appMockup.tabs.ai'), slug: 'ai', asset: aiAsset, needsScroll: true },
  ];
  const activeMockupTab = mockupTabs[activeTab] ?? mockupTabs[0];

  useEffect(() => {
    const path = location.pathname.split('/')[1] || 'home';
    setCurrentPage(path);
  }, [location]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('cc_preview_api');
      if (raw) {
        const parsed = JSON.parse(raw);
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - (parsed.timestamp || 0) > sevenDays) {
          localStorage.removeItem('cc_preview_api');
        }
      }
    } catch {
      localStorage.removeItem('cc_preview_api');
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [currentPage]);

  useEffect(() => {
    if (currentPage !== 'home') {
      ScrollTrigger.getAll().forEach(t => t.kill());
    }
  }, [currentPage]);

  useEffect(() => {
    if (currentPage === 'home') {
      heroAnimReadyRef.current = false;
      const timeout = setTimeout(() => {
        heroAnimReadyRef.current = true;
        setHeroAnimKey((k) => k + 1);
        setBrokerAnimKey((k) => k + 1);
        ctaHasRunRef.current = false;
        testimonialsEntranceDoneRef.current = false;
        
        // Reset animations when returning to landing page
        ScrollTrigger.refresh();
        
        // Reset stats counter values
        if (tradersNumberRef.current) {
          tradersNumberRef.current.textContent = '0+';
          tradersNumberRef.current.style.opacity = '0';
        }
        if (volumeNumberRef.current) {
          volumeNumberRef.current.textContent = '0+';
          volumeNumberRef.current.style.opacity = '0';
        }
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [currentPage]);



  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Element && !event.target.closest('.broker-trigger') && !event.target.closest('.wallet-trigger')) {
        setActiveBroker(null);
        setActiveWallet(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 200);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  useGSAP(() => {
    const startTime = performance.now();
    const angles: Record<BrokerKey, number> = {
      binance: 0,
      okx: 51.4,
      indodax: 102.8,
      mexc: 154.2,
      bitget: 205.6,
      tokocrypto: 257.0,
      bybit: 308.4,
    };
    const tick = () => {
      const isMobile = window.innerWidth < 768;
      let rX = 300;
      let rY = 100;
      if (isMobile) {
        const svgWidth = Math.min(window.innerWidth * 0.9, 340);
        const scale = svgWidth / 340;
        rX = 160 * scale;
        rY = 60 * scale;
      }
      brokerKeys.forEach((key) => {
        angles[key] += 360 / (brokerConfigs[key].duration * 60);
        const rad = (angles[key] * Math.PI) / 180;
        const x = Math.cos(rad) * rX;
        const y = Math.sin(rad) * rY;
        const depth = Math.sin(rad);
        const scale = 0.8 + (depth + 1) * 0.1;
        const opacity = 0.6 + (depth + 1) * 0.2;
        const zIndex = depth > 0 ? 20 : 8;
        const el = brokerRefs[key].current;
        if (!el) return;
        el.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${scale})`;
        el.style.opacity = String(opacity);
        el.style.zIndex = String(zIndex);
      });
    };
    let tickerActive = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !tickerActive) {
            gsap.ticker.add(tick);
            gsap.ticker.fps(60);
            tickerActive = true;
          } else if (!entry.isIntersecting && tickerActive) {
            gsap.ticker.remove(tick);
            tickerActive = false;
          }
        });
      },
      { threshold: 0.1 }
    );
    if (brokerStageRef.current) observer.observe(brokerStageRef.current);
    return () => {
      observer.disconnect();
      if (tickerActive) gsap.ticker.remove(tick);
    };
  }, { dependencies: [brokerAnimKey, heroAnimKey] });

  // Combined orbit animation - Single orbit with 14 icons (7 CEX + 7 Wallets)
  useGSAP(() => {
    const startTime = performance.now();
    
    // Initial angles for 14 icons evenly distributed (360/14 = 25.71° spacing)
    const allAngles: Record<OrbitIconKey, number> = {
      binance: 0,        // CEX
      metamask: 25.71,   // Wallet
      okx: 51.43,        // CEX
      phantom: 77.14,    // Wallet
      indodax: 102.86,   // CEX
      coinbase: 128.57,  // Wallet
      mexc: 154.29,      // CEX
      trustwallet: 180,  // Wallet
      bitget: 205.71,    // CEX
      dexscreener: 231.43, // Wallet
      tokocrypto: 257.14, // CEX
      solflare: 282.86,  // Wallet
      bybit: 308.57,     // CEX
      okxwallet: 334.29, // Wallet
    };
    
    const tick = () => {
      const isMobile = window.innerWidth < 768;
      // Single orbit - slightly larger for more spacing
      let rX = 320;
      let rY = 106;
      if (isMobile) {
        const svgWidth = Math.min(window.innerWidth * 0.9, 340);
        const scale = svgWidth / 340;
        rX = 170 * scale;
        rY = 63 * scale;
      }
      
      allOrbitKeys.forEach((key) => {
        const isBroker = brokerKeys.includes(key as BrokerKey);
        const duration = isBroker ? brokerConfigs[key as BrokerKey].duration : walletConfigs[key as WalletKey].duration;
        
        allAngles[key] += 360 / (duration * 60);
        const rad = (allAngles[key] * Math.PI) / 180;
        const x = Math.cos(rad) * rX;
        const y = Math.sin(rad) * rY;
        const depth = Math.sin(rad);
        
        // Consistent sizing for all icons
        const scale = 0.8 + (depth + 1) * 0.1;
        const opacity = 0.6 + (depth + 1) * 0.2;
        const zIndex = depth > 0 ? 20 : 8;
        
        const ref = isBroker ? brokerRefs[key as BrokerKey] : walletRefs[key as WalletKey];
        const el = ref.current;
        
        if (!el) return;
        el.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${scale})`;
        el.style.opacity = String(opacity);
        el.style.zIndex = String(zIndex);
      });
    };
    
    let tickerActive = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !tickerActive) {
            gsap.ticker.add(tick);
            gsap.ticker.fps(60);
            tickerActive = true;
          } else if (!entry.isIntersecting && tickerActive) {
            gsap.ticker.remove(tick);
            tickerActive = false;
          }
        });
      },
      { threshold: 0.1 }
    );
    if (brokerStageRef.current) observer.observe(brokerStageRef.current);
    return () => {
      observer.disconnect();
      if (tickerActive) gsap.ticker.remove(tick);
    };
  }, { dependencies: [brokerAnimKey, heroAnimKey] });

  useGSAP(() => {
    if (!meshBlob1Ref.current || !meshBlob2Ref.current || !meshBlob3Ref.current) return;

    const isMobile = window.innerWidth < 768;

    gsap.to(meshBlob1Ref.current, {
      x: isMobile ? -45 : -90,
      y: isMobile ? 21 : 42,
      scale: isMobile ? 1.1 : 1.18,
      duration: isMobile ? 10 : 8,
      delay: 0,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });

    gsap.to(meshBlob2Ref.current, {
      x: isMobile ? 55 : 110,
      y: isMobile ? -17 : -34,
      scale: isMobile ? 1.15 : 1.28,
      duration: isMobile ? 12 : 10,
      delay: 0.8,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });

    gsap.to(meshBlob3Ref.current, {
      x: isMobile ? 31 : 62,
      y: isMobile ? 38 : 76,
      scale: isMobile ? 1.08 : 1.16,
      duration: isMobile ? 9 : 6.5,
      delay: 1.4,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut',
    });
  }, { scope: heroSectionRef, dependencies: [heroAnimKey] });

  useGSAP(() => {
    const container = heroContentRef.current;
    if (!container) return;

    const badge = container.querySelector("[data-hero-badge]");
    const titleEl = container.querySelector<HTMLElement>("[data-hero-title]");
    const cursorEl = container.querySelector<HTMLElement>("[data-hero-cursor]");
    const subtitle = container.querySelector("[data-hero-subtitle]");
    const cta = container.querySelector("[data-hero-cta]");

    if (!titleEl || !cursorEl) return;

    if (!heroAnimReadyRef.current) return;
    if (!heroTitle || heroTitle === "hero.title") return;

    gsap.killTweensOf(titleEl);
    gsap.killTweensOf(cursorEl);
    titleEl.textContent = "";
    gsap.set(titleEl, { opacity: 0 });
    gsap.set(cursorEl, { opacity: 0 });

    const cleanups: (() => void)[] = [];
    let loopTimeout: ReturnType<typeof setTimeout> | null = null;
    let isDestroyed = false;
    const capturedTitle = currentActiveTitleRef.current;

    gsap.to(badge, { opacity: 1, duration: 1.0, ease: "power2.out", delay: 0.3 });
    gsap.to(subtitle, { opacity: 1, duration: 1.0, ease: "power2.out", delay: 0.6 });
    gsap.to(cta, { opacity: 1, duration: 1.0, ease: "power2.out", delay: 0.9 });

    const runLoop = (titleOverride?: string) => {
      if (isDestroyed) return;
      gsap.killTweensOf(titleEl);
      gsap.killTweensOf(cursorEl);
      titleEl.textContent = "";
      gsap.set(titleEl, { opacity: 1 });
      gsap.set(cursorEl, { opacity: 1 });

      const cleanupType = typeText(titleEl, titleOverride ?? capturedTitle, HERO_TYPE_MS, () => {
        if (isDestroyed) return;
        loopTimeout = setTimeout(() => {
          if (isDestroyed) return;
          gsap.set(cursorEl, { opacity: 0 });
          const cleanupDelete = deleteText(titleEl, HERO_DELETE_MS, () => {
            if (isDestroyed) return;
            const next = pendingTitleRef.current || currentActiveTitleRef.current;
            pendingTitleRef.current = null;
            loopTimeout = setTimeout(() => runLoop(next), HERO_PAUSE_AFTER_DELETE_MS);
          });
          cleanups.push(cleanupDelete);
        }, HERO_PAUSE_AFTER_TYPE_MS);
      });
      cleanups.push(cleanupType);
    };

    runLoop();

    return () => {
      isDestroyed = true;
      cleanups.forEach((fn) => fn());
      if (loopTimeout) clearTimeout(loopTimeout);
      gsap.killTweensOf(titleEl);
      gsap.killTweensOf(cursorEl);
      titleEl.textContent = "";
      gsap.set(titleEl, { opacity: 0 });
      gsap.set(cursorEl, { opacity: 0 });
    };
  }, { scope: heroContentRef, dependencies: [heroAnimKey] });

  useGSAP(() => {
    const section = testimonialsSectionRef.current;
    if (!section) {
      return;
    }

    const badge = section.querySelector<HTMLElement>('[data-test-badge]');
    const titleWords = gsap.utils.toArray<HTMLElement>('[data-test-title-word]', section);
    const desc = section.querySelector<HTMLElement>('[data-test-desc]');
    const divider = section.querySelector<HTMLElement>('[data-test-divider]');
    const join = section.querySelector<HTMLElement>('[data-test-join]');
    const leftCards = gsap.utils.toArray<HTMLElement>('[data-test-card-left]', section);
    const rightCards = gsap.utils.toArray<HTMLElement>('[data-test-card-right]', section);
    const trustItems = gsap.utils.toArray<HTMLElement>('[data-test-trust]', section);

    const cleanups: (() => void)[] = [];
    let isDestroyed = false;

    const startTyping = () => {
      let wordIndex = 0;
      const originalWords = titleWords.map((w) => w.textContent ?? "");

      titleWords.forEach((w) => {
        w.textContent = "";
        gsap.set(w, { y: "0%", opacity: 1 });
      });

      const typeNext = () => {
        if (isDestroyed) return;
        if (wordIndex >= titleWords.length) return;

        const el = titleWords[wordIndex];
        const text = originalWords[wordIndex];
        const cleanup = typeText(el, text, TESTIMONIALS_TYPE_MS, () => {
          if (isDestroyed) return;
          wordIndex++;
          const timeoutId = setTimeout(typeNext, TESTIMONIALS_WORD_GAP_MS);
          cleanups.push(() => clearTimeout(timeoutId));
        });
        cleanups.push(cleanup);
      };

      typeNext();
    };

    const titleDuration = 0.3 + Math.max(titleWords.length - 1, 0) * 0.15 + 0.8;
    const headerTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        once: false,
      },
    });

    headerTimeline
      .fromTo(badge, { x: -60, opacity: 0 }, { x: 0, opacity: 1, duration: 0.9, ease: 'power3.out' })
      .add(() => {
        startTyping();
      }, 0.35)
      .fromTo(desc, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, titleDuration)
      .fromTo(divider, { scaleX: 0, opacity: 0 }, { scaleX: 1, opacity: 1, transformOrigin: 'left center', duration: 0.8, ease: 'power3.out' }, titleDuration + 0.15)
      .fromTo(join, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, titleDuration + 0.3);

    testimonialsEntranceDoneRef.current = false;

    gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 75%',
        once: false,
        onEnter: () => {
          testimonialsEntranceDoneRef.current = false;
        },
      },
      onComplete: () => {
        testimonialsEntranceDoneRef.current = true;
      },
    })
      .fromTo(leftCards, { y: 80, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, stagger: 0.12, ease: 'power3.out' }, 0)
      .fromTo(rightCards, { y: -80, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, stagger: 0.12, ease: 'power3.out' }, 0);

    gsap.fromTo(
      trustItems,
      { x: 30, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.75,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 75%',
          once: false,
        },
      },
    );

    return () => {
      isDestroyed = true;
      cleanups.forEach((fn) => fn());
    };
  }, { scope: testimonialsSectionRef, dependencies: [heroAnimKey] });

  useGSAP(() => {
    const section = testimonialsSectionRef.current;
    const grid = testimonialsGridRef.current;
    if (!section || !grid) {
      return;
    }

    const spotlightTrigger = ScrollTrigger.create({
      trigger: grid,
      start: 'top 60%',
      end: 'bottom top',
      onUpdate: (self) => {
        if (!testimonialsEntranceDoneRef.current) {
          return;
        }

        const allCards = gsap.utils.toArray<HTMLElement>('[data-test-card-index]', section)
          .sort((a, b) => Number(a.getAttribute('data-test-card-index')) - Number(b.getAttribute('data-test-card-index')));
        const totalCards = allCards.length;
        if (totalCards === 0) {
          return;
        }

        const progress = self.progress;
        const spotlightIndex = progress * (totalCards - 1);

        allCards.forEach((card, i) => {
          const distance = Math.abs(i - spotlightIndex);
          const opacity = distance < 0.8 ? 1 : distance < 2 ? 1 - (distance - 0.8) / 1.2 * 0.65 : 0.35;
          const scale = distance < 0.8 ? 1 : distance < 2 ? 1 - (distance - 0.8) / 1.2 * 0.03 : 0.97;
          gsap.to(card, { opacity, scale, duration: 0.3, ease: 'power2.out', overwrite: 'auto' });
        });
      },
    });

    return () => {
      spotlightTrigger.kill();
    };
  }, { scope: testimonialsSectionRef, dependencies: [heroAnimKey] });

  useGSAP(() => {
    const section = partnerSectionRef.current;
    if (!section) return;

    const features = gsap.utils.toArray<HTMLElement>('[data-partner-features]', section);
    const cta = section.querySelector('[data-partner-cta]');
    const badgeLogo = section.querySelector<HTMLElement>('[data-partner-badge-logo]');
    const badgeWords = gsap.utils.toArray<HTMLElement>('[data-partner-badge-word]', section);
    const titleWords = gsap.utils.toArray<HTMLElement>('[data-partner-title-word]', section);

    const cleanups: (() => void)[] = [];
    let isDestroyed = false;

    const startBadgeTyping = () => {
      let wordIndex = 0;
      const originalWords = badgeWords.map((w) => (w as HTMLElement).textContent ?? "");

      badgeWords.forEach((w) => {
        (w as HTMLElement).textContent = "";
      });

      const typeNext = () => {
        if (isDestroyed) return;
        if (wordIndex >= badgeWords.length) return;

        const el = badgeWords[wordIndex] as HTMLElement;
        const text = originalWords[wordIndex];
        const cleanup = typeText(el, text, PARTNER_TYPE_MS, () => {
          if (isDestroyed) return;
          wordIndex++;
          const timeoutId = setTimeout(typeNext, PARTNER_WORD_GAP_MS);
          cleanups.push(() => clearTimeout(timeoutId));
        });
        cleanups.push(cleanup);
      };

      typeNext();
    };

    const startTitleTyping = () => {
      const fullText = titleWords.map((w) => (w as HTMLElement).textContent ?? "").join(" ");
      const allWords = titleWords as HTMLElement[];
      allWords.forEach((w) => { w.textContent = ""; });

      let charIndex = 0;
      let wordCursor = 0;
      let charInWord = 0;
      const wordTexts = fullText.split(" ");

      const id = setInterval(() => {
        if (isDestroyed) { clearInterval(id); return; }
        if (wordCursor >= allWords.length) { clearInterval(id); return; }

        const currentWordEl = allWords[wordCursor];
        const currentWordText = wordTexts[wordCursor] ?? "";

        charInWord++;
        currentWordEl.textContent = currentWordText.slice(0, charInWord);

        if (charInWord >= currentWordText.length) {
          wordCursor++;
          charInWord = 0;
        }

        charIndex++;
      }, HERO_TYPE_MS);

      cleanups.push(() => clearInterval(id));
    };

    gsap.timeline({
      scrollTrigger: { trigger: section, start: 'top 75%', once: false },
    })
      .add(() => {
        gsap.fromTo(badgeLogo, { x: -28, opacity: 0 }, { x: 0, opacity: 1, duration: 1.0, ease: 'power2.out' });
        startBadgeTyping();
      }, 0)
      .add(() => {
        startTitleTyping();
      }, 0.2)
      .fromTo(features, { x: -22, opacity: 0 }, { x: 0, opacity: 1, stagger: 0.18, duration: 1.0, ease: 'power2.out' }, 0.55)
      .fromTo(cta, { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 1.0, ease: 'power2.out' }, 1.1);

    let cardFloat: gsap.core.Tween | null = null;
    let badgeFloat: gsap.core.Tween | null = null;
    let cardEntrance: gsap.core.Tween | null = null;
    let badgeEntrance: gsap.core.Tween | null = null;
    let exitTween: gsap.core.Timeline | null = null;

    const entranceTrigger = ScrollTrigger.create({
      trigger: section,
      start: "top 75%",
      onEnter: () => {
        if (cardEntrance) cardEntrance.kill();
        if (badgeEntrance) badgeEntrance.kill();
        if (cardFloat) cardFloat.kill();
        if (badgeFloat) badgeFloat.kill();
        if (exitTween) exitTween.kill();

        gsap.set(partnerCardRef.current, { rotation: -6, opacity: 0, y: 40 });
        gsap.set(partnerBadgeRef.current, { opacity: 0, y: 20 });

        cardEntrance = gsap.to(partnerCardRef.current, {
          opacity: 1,
          y: 0,
          duration: 3.2,
          ease: "power3.out",
          onComplete: () => {
            cardFloat = gsap.to(partnerCardRef.current, {
              y: PARTNER_CARD_FLOAT_Y,
              duration: PARTNER_CARD_FLOAT_DURATION,
              repeat: -1,
              yoyo: true,
              ease: "sine.inOut",
            });
          },
        });

        badgeEntrance = gsap.to(partnerBadgeRef.current, {
          opacity: 1,
          y: 0,
          duration: 2.8,
          ease: "power3.out",
          delay: 1.2,
          onComplete: () => {
            badgeFloat = gsap.to(partnerBadgeRef.current, {
              y: PARTNER_BADGE_FLOAT_Y,
              duration: PARTNER_BADGE_FLOAT_DURATION,
              repeat: -1,
              yoyo: true,
              ease: "sine.inOut",
            });
          },
        });
      },
      onLeaveBack: () => {
        if (cardEntrance) cardEntrance.kill();
        if (badgeEntrance) badgeEntrance.kill();
        if (cardFloat) cardFloat.kill();
        if (badgeFloat) badgeFloat.kill();
        if (exitTween) exitTween.kill();

        gsap.set(partnerCardRef.current, { opacity: 0, y: 40 });
        gsap.set(partnerBadgeRef.current, { opacity: 0, y: 20 });
      },
    });

    const exitTrigger = ScrollTrigger.create({
      trigger: section,
      start: "bottom bottom",
      onEnter: () => {
        if (cardFloat) cardFloat.kill();
        if (badgeFloat) badgeFloat.kill();
        if (cardEntrance) cardEntrance.kill();
        if (badgeEntrance) badgeEntrance.kill();
        if (exitTween) exitTween.kill();

        exitTween = gsap.timeline();
        exitTween.to(partnerCardRef.current, {
          opacity: 0,
          y: 20,
          duration: 1.2,
          ease: "power2.in"
        }, 0);
        exitTween.to(partnerBadgeRef.current, {
          opacity: 0,
          y: 12,
          duration: 1.2,
          ease: "power2.in"
        }, 0);
      },
      onLeaveBack: () => {
        if (exitTween) exitTween.kill();
        if (cardFloat) cardFloat.kill();
        if (badgeFloat) badgeFloat.kill();

        gsap.to(partnerCardRef.current, {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power2.out",
          onComplete: () => {
            cardFloat = gsap.to(partnerCardRef.current, {
              y: PARTNER_CARD_FLOAT_Y,
              duration: PARTNER_CARD_FLOAT_DURATION,
              repeat: -1,
              yoyo: true,
              ease: "sine.inOut"
            });
          }
        });

        gsap.to(partnerBadgeRef.current, {
          opacity: 1,
          y: 0,
          duration: 1.2,
          ease: "power2.out",
          onComplete: () => {
            badgeFloat = gsap.to(partnerBadgeRef.current, {
              y: PARTNER_BADGE_FLOAT_Y,
              duration: PARTNER_BADGE_FLOAT_DURATION,
              repeat: -1,
              yoyo: true,
              ease: "sine.inOut"
            });
          }
        });
      }
    });

    return () => {
      isDestroyed = true;
      cleanups.forEach((fn) => fn());
      entranceTrigger.kill();
      exitTrigger.kill();
      if (cardEntrance) cardEntrance.kill();
      if (badgeEntrance) badgeEntrance.kill();
      if (cardFloat) cardFloat.kill();
      if (badgeFloat) badgeFloat.kill();
      if (exitTween) exitTween.kill();
    };
  }, { scope: partnerSectionRef, dependencies: [heroAnimKey] });

  useGSAP(() => {
    const section = ctaSectionRef.current;
    const headline = ctaHeadlineRef.current;
    if (!section || !headline) return;

    const title1El = headline.querySelector<HTMLElement>('[data-cta-title1]');
    const title2El = headline.querySelector<HTMLElement>('[data-cta-title2]');
    const cursor = headline.querySelector<HTMLElement>('[data-cta-cursor]');

    if (!title1El || !title2El || !cursor) return;

    let isDestroyed = false;
    let activeIntervalId: ReturnType<typeof setInterval> | null = null;
    const delayedCalls: gsap.core.Tween[] = [];

    const clearActive = () => {
      if (activeIntervalId) {
        clearInterval(activeIntervalId);
        activeIntervalId = null;
      }
      delayedCalls.forEach((d) => d.kill());
      delayedCalls.length = 0;
    };

    const typeEl = (el: HTMLElement, text: string, cb: () => void) => {
      let i = 0;
      el.textContent = '';
      activeIntervalId = setInterval(() => {
        if (isDestroyed) {
          if (activeIntervalId) clearInterval(activeIntervalId);
          return;
        }
        i++;
        el.textContent = text.slice(0, i);
        if (i >= text.length) {
          if (activeIntervalId) clearInterval(activeIntervalId);
          activeIntervalId = null;
          cb();
        }
      }, 75);
    };

    const runCtaAnim = (lang: string) => {
      if (isDestroyed) return;

      const ft = i18n.getFixedT(lang);
      const t1 = ft('cta.title1');
      const t2 = ft('cta.title2');

      title1El.textContent = '';
      title2El.textContent = '';
      gsap.set(cursor, { opacity: 1 });
      ctaIsTypingRef.current = true;

      typeEl(title1El, t1, () => {
        if (isDestroyed) return;
        const dc1 = gsap.delayedCall(0.15, () => {
          typeEl(title2El, t2, () => {
            if (isDestroyed) return;
            ctaIsTypingRef.current = false;
            if (ctaPendingLangRef.current) {
              const next = ctaPendingLangRef.current;
              ctaPendingLangRef.current = null;
              const dc3 = gsap.delayedCall(0.3, () => runCtaAnim(next));
              delayedCalls.push(dc3);
            } else {
              const dc2 = gsap.delayedCall(0.6, () => {
                if (!isDestroyed) {
                  gsap.to(cursor, { opacity: 0, duration: 0.4, ease: 'power2.out' });
                }
              });
              delayedCalls.push(dc2);
            }
          });
        });
        delayedCalls.push(dc1);
      });
    };

    ctaHasRunRef.current = false;
    ctaIsTypingRef.current = false;
    ctaPendingLangRef.current = null;
    ctaRunAnimRef.current = runCtaAnim;

    ScrollTrigger.create({
      trigger: section,
      start: 'top 75%',
      onEnter: () => {
        if (ctaHasRunRef.current) return;
        ctaHasRunRef.current = true;
        runCtaAnim(i18n.language);
      },
    });

    return () => {
      isDestroyed = true;
      clearActive();
    };
  }, { scope: ctaSectionRef, dependencies: [heroAnimKey] });

  const ctaRunAnimRef = React.useRef<((lang: string) => void) | null>(null);

  useEffect(() => {
    if (!ctaHasRunRef.current) return;
    if (ctaIsTypingRef.current) {
      ctaPendingLangRef.current = i18n.language;
      return;
    }
    if (ctaRunAnimRef.current) {
      ctaRunAnimRef.current(i18n.language);
    }
  }, [i18n.language]);

  useGSAP(() => {
    if (!heroRingRefs.current.some(Boolean)) return;

    const isMobile = window.innerWidth < 768;

    const outerRings = heroRingRefs.current.slice(0, 4).filter((ring): ring is HTMLDivElement => Boolean(ring));
    const breathRing = heroRingRefs.current[4];

    gsap.fromTo(
      outerRings,
      { scale: 0.7, opacity: isMobile ? 0.3 : 0.5, rotation: 0 },
      {
        scale: isMobile ? 1.1 : 1.2,
        opacity: 0,
        rotation: isMobile ? 30 : 55,
        duration: isMobile ? 8 : 6,
        ease: 'power2.out',
        repeat: -1,
        stagger: isMobile ? 2 : 1.4,
      },
    );

    outerRings.forEach((ring) => {
      gsap.to(ring, {
        rotation: 360,
        duration: isMobile ? 60 : 40,
        repeat: -1,
        ease: 'none',
      });
    });

    if (breathRing) {
      gsap.to(breathRing, {
        scale: 1.04,
        opacity: isMobile ? 0.2 : 0.35,
        duration: isMobile ? 4 : 3,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }
  }, { dependencies: [heroAnimKey] });

  useGSAP(() => {
    const section = featuresSectionRef.current;
    if (!section) return;

    setActiveFeature(0);

    const totalPanels = 4;

    ScrollTrigger.create({
      trigger: section,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => {
        const index = Math.min(
          totalPanels - 1,
          Math.floor(self.progress * totalPanels)
        );
        setActiveFeature(index);
      },
    });
  }, { dependencies: [heroAnimKey] });

  useEffect(() => {
    if (currentPage !== 'home') {
      setActiveFeature(0);
    }
  }, [currentPage]);

  useGSAP(() => {
    const statsBar = statsBarRef.current;
    const tradersNumber = tradersNumberRef.current;
    const volumeNumber = volumeNumberRef.current;

    if (!statsBar || !tradersNumber || !volumeNumber) {
      return;
    }

    gsap.set([tradersNumberRef.current, volumeNumberRef.current], { opacity: 0 });

    tradersNumber.textContent = '0+';
    volumeNumber.textContent = '0+';

    const tradersCounter = { val: 0 };
    const volumeCounter = { val: 0 };

    gsap.to(tradersCounter, {
      val: 3000,
      duration: 2.5,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: statsBar,
        start: 'top 85%',
        once: false,
      },
      onStart: () => {
        gsap.set(tradersNumberRef.current, { opacity: 1 });
      },
      onUpdate: () => {
        tradersNumber.textContent = `${Math.round(tradersCounter.val).toLocaleString('en-US')}+`;
      },
    });

    gsap.to(volumeCounter, {
      val: 45000,
      duration: 2.5,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: statsBar,
        start: 'top 85%',
        once: false,
      },
      onStart: () => {
        gsap.set(volumeNumberRef.current, { opacity: 1 });
      },
      onUpdate: () => {
        volumeNumber.textContent = `${Math.round(volumeCounter.val).toLocaleString('en-US')}+`;
      },
    });
  }, { dependencies: [heroAnimKey] });

  useGSAP(() => {
    const section = compSectionRef.current;
    const leftBg = compLeftBgRef.current;
    const rightBg = compRightBgRef.current;

    if (!section || !leftBg || !rightBg) {
      return;
    }

    const badge = section.querySelector('[data-comp-badge]');
    const title = section.querySelector('[data-comp-title]');
    const subtitle = section.querySelector('[data-comp-subtitle]');
    const left = section.querySelector('[data-comp-left]');
    const right = section.querySelector('[data-comp-right]');
    const cta = section.querySelector('[data-comp-cta]');

    if (comp42Ref.current) comp42Ref.current.textContent = '0%';
    if (comp89Ref.current) comp89Ref.current.textContent = '0%';

    gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        once: false,
      },
    })
      .fromTo(leftBg, { x: '-100%', opacity: 0 }, { x: '0%', opacity: 1, duration: 1.2, ease: 'power3.out' }, 0)
      .fromTo(rightBg, { x: '100%', opacity: 0 }, { x: '0%', opacity: 1, duration: 1.2, ease: 'power3.out' }, 0)
      .fromTo(badge, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, 0.3)
      .fromTo(title, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, 0.5)
      .fromTo(subtitle, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, 0.7)
      .fromTo(left, { x: -60, opacity: 0 }, { x: 0, opacity: 1, duration: 1, ease: 'power3.out' }, 0.4)
      .fromTo(right, { x: 60, opacity: 0 }, { x: 0, opacity: 1, duration: 1, ease: 'power3.out' }, 0.6)
      .fromTo(cta, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }, 1);

    const counter42 = { val: 0 };
    const counter89 = { val: 0 };

    gsap.to(counter42, {
      val: 42,
      duration: 2,
      ease: 'power2.out',
      delay: 0.8,
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        once: false,
      },
      onUpdate: () => {
        if (comp42Ref.current) {
          comp42Ref.current.textContent = `${Math.round(counter42.val)}%`;
        }
      },
    });

    gsap.to(counter89, {
      val: 89,
      duration: 2,
      ease: 'power2.out',
      delay: 0.8,
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        once: false,
      },
      onUpdate: () => {
        if (comp89Ref.current) {
          comp89Ref.current.textContent = `${Math.round(counter89.val)}%`;
        }
      },
    });
  }, { dependencies: [heroAnimKey] });

  useEffect(() => {
    if (currentPage !== 'home') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    const timeout = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 300);
    return () => clearTimeout(timeout);
  }, [heroAnimKey]);

  useGSAP(() => {
    const section = mockupSectionRef.current;
    if (!section) return;

    const copy = gsap.utils.toArray<HTMLElement>('[data-mockup-copy]', section);
    const tabBar = section.querySelector('[data-mockup-tabs]');
    const frame = section.querySelector('[data-mockup-frame]');

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top 75%',
        once: false,
      },
    })
      .fromTo(copy, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, stagger: 0.14, ease: 'power3.out' })
      .fromTo(tabBar, { y: 32, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, ease: 'power3.out' }, 0.35)
      .fromTo(frame, { y: 80, opacity: 0 }, { y: 0, opacity: 1, duration: 1.2, ease: 'power3.out' }, 0.5);

    mockupTlRef.current = tl;
  }, { scope: mockupSectionRef, dependencies: [heroAnimKey] });

  useEffect(() => {
    const images = imgRefs.current.filter((img): img is HTMLImageElement => Boolean(img));
    images.forEach((img) => gsap.killTweensOf(img));
    gsap.set(images, { y: 0 });

    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      return () => {
        images.forEach((img) => gsap.killTweensOf(img));
      };
    }

    const active = mockupTabs[activeTab];
    const imgEl = imgRefs.current[activeTab];
    const frameEl = frameContentRef.current;

    if (!active?.needsScroll || !imgEl || !frameEl) {
      return () => {
        images.forEach((img) => gsap.killTweensOf(img));
      };
    }

    const timeout = setTimeout(() => {
      const containerHeight = frameEl.getBoundingClientRect().height;
      const renderedImgHeight = imgEl.offsetWidth * (imgEl.naturalHeight / imgEl.naturalWidth);
      const scrollDist = Math.max(0, Math.min(renderedImgHeight - containerHeight, 300));

      if (scrollDist > 0) {
        gsap.to(imgEl, { y: -scrollDist, duration: 25, ease: "sine.inOut", repeat: -1, yoyo: true });
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
      images.forEach((img) => gsap.killTweensOf(img));
    };
  }, [activeTab]);

  const handleBrokerClick = (broker: BrokerKey) => {
    setActiveBroker((current) => current === broker ? null : broker);
  };

  const handleWalletClick = (wallet: WalletKey) => {
    setActiveWallet((current) => current === wallet ? null : wallet);
  };

  const handleOrbitToggle = () => setOrbitGlowActive((v) => !v);


  const renderBrokerPopup = (broker: BrokerKey) => {
    const info = brokerInfo[broker];
    const isActive = activeBroker === broker;

    return (
      <motion.div
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        initial={false}
        animate={isActive ? { opacity: 1, y: 0, scale: 1, pointerEvents: 'auto' } : { opacity: 0, y: -8, scale: 0.95, pointerEvents: 'none' }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="absolute top-[calc(100%+0.75rem)] left-1/2 z-30 w-56 -translate-x-1/2 rounded-xl border border-white/10 bg-[#0A0A0C] p-4 text-left shadow-[0_18px_45px_rgba(0,0,0,0.45)]"
      >
        <div className="text-sm font-bold text-white">{info.name}</div>
        <div className="mt-1 text-xs leading-relaxed text-neutral-400">{info.description}</div>
        <a
          href={`https://${info.website}`}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex text-xs font-semibold text-[#E26A59] hover:text-[#FF6B55] transition-colors"
        >
          {info.website}
        </a>
      </motion.div>
    );
  };

  const renderWalletPopup = (wallet: WalletKey) => {
    const info = walletInfo[wallet];
    const isActive = activeWallet === wallet;

    return (
      <motion.div
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        initial={false}
        animate={isActive ? { opacity: 1, y: 0, scale: 1, pointerEvents: 'auto' } : { opacity: 0, y: -8, scale: 0.95, pointerEvents: 'none' }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="absolute top-[calc(100%+0.75rem)] left-1/2 z-30 w-56 -translate-x-1/2 rounded-xl border border-white/10 bg-[#0A0A0C] p-4 text-left shadow-[0_18px_45px_rgba(0,0,0,0.45)]"
      >
        <div className="text-sm font-bold text-white">{info.name}</div>
        <div className="mt-1 text-xs leading-relaxed text-neutral-400">{info.description}</div>
        <a
          href={`https://${info.website}`}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex text-xs font-semibold text-[#E26A59] hover:text-[#FF6B55] transition-colors"
        >
          {info.website}
        </a>
      </motion.div>
    );
  };



  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleOpenVideoModal = () => {
    setIsVideoModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    document.body.style.overflow = ''; // Restore scroll
  };

  // Close modal on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVideoModalOpen) {
        handleCloseVideoModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isVideoModalOpen]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleFeatureClick = (tabIndex: number) => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      setCurrentPage('home');
    }
    setActiveTab(tabIndex);

    // Skip mockup animation instantly when clicked from outside (e.g. Hero Demo button, Footer links)
    if (mockupTlRef.current) {
      if (mockupTlRef.current.scrollTrigger) {
        mockupTlRef.current.scrollTrigger.kill();
      }
      mockupTlRef.current.progress(1);
    } else {
      const section = mockupSectionRef.current;
      if (section) {
        const copy = gsap.utils.toArray<HTMLElement>('[data-mockup-copy]', section);
        const tabBar = section.querySelector('[data-mockup-tabs]');
        const frame = section.querySelector('[data-mockup-frame]');
        gsap.set(copy, { y: 0, opacity: 1 });
        gsap.set(tabBar, { y: 0, opacity: 1 });
        gsap.set(frame, { y: 0, opacity: 1 });
      }
    }

    setTimeout(() => {
      const section = mockupSectionRef.current;
      if (!section) return;
      const lenis = lenisRef.current;
      if (lenis) {
        lenis.scrollTo(section, { offset: 0, duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
      } else {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  };

  const handleNavFeatureClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      setCurrentPage('home');
    }
    setTimeout(() => {
      const section = featuresSectionRef.current;
      if (!section) return;
      const lenis = lenisRef.current;
      if (lenis) {
        lenis.scrollTo(section, { offset: 0, duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
      } else {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  };

  return (
    <div className="min-h-screen bg-[#050505] text-neutral-200 font-sans selection:bg-red-500/30 flex flex-col">
      {/* NAVBAR */}
      <Navbar
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        currentPage={currentPage}
        setCurrentPage={handleSetPage}
        onLogin={handleLogin}
        onGetStarted={handleGetStarted}
        onFeatureClick={handleFeatureClick}
        onNavFeatureClick={handleNavFeatureClick}
        onPricingClick={() => {}}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentPage}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
          className="flex-1 flex flex-col bg-[#050505]"
        >
          {currentPage === 'home' ? (
        <main className="flex-1">
          {/* HERO SECTION */}
          <section ref={heroSectionRef} className="relative overflow-hidden flex flex-col pt-32">
            {/* Background glow & circles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-start justify-center mt-[-100px]">
              <div
                ref={meshBlob1Ref}
                className="absolute top-[-20px] left-1/2 w-[300px] h-[300px] md:w-[600px] md:h-[600px] -translate-x-1/2 rounded-full bg-[#6b0000] blur-[80px] md:blur-[180px] mix-blend-screen opacity-70"
              />
              <div
                ref={meshBlob2Ref}
                className="absolute top-[-80px] left-[42%] w-[200px] h-[200px] md:w-[400px] md:h-[400px] -translate-x-1/2 rounded-full bg-[#3d0000] blur-[60px] md:blur-[140px] mix-blend-screen opacity-65"
              />
              <div
                ref={meshBlob3Ref}
                className="absolute top-[80px] left-[58%] w-[250px] h-[250px] md:w-[500px] md:h-[500px] -translate-x-1/2 rounded-full bg-[#1a0000] blur-[50px] md:blur-[120px] mix-blend-screen opacity-75"
              />
              {/* Concentric Circles */}
              <div
                ref={(node) => {
                  heroRingRefs.current[4] = node;
                }}
                className="absolute top-[-200px] w-[500px] h-[500px] border border-white/30 rounded-full"
              />
              <div
                ref={(node) => {
                  heroRingRefs.current[0] = node;
                }}
                className="absolute top-[-300px] w-[400px] h-[400px] md:w-[800px] md:h-[800px] border-2 border-white/30 md:border-white/60 rounded-full opacity-70"
              />
              <div
                ref={(node) => {
                  heroRingRefs.current[1] = node;
                }}
                className="absolute top-[-400px] w-[550px] h-[550px] md:w-[1100px] md:h-[1100px] border-2 border-white/25 md:border-white/55 rounded-full opacity-70"
              />
              <div
                ref={(node) => {
                  heroRingRefs.current[2] = node;
                }}
                className="absolute top-[-500px] w-[700px] h-[700px] md:w-[1400px] md:h-[1400px] border-2 border-white/20 md:border-white/50 rounded-full opacity-70"
              />
              <div
                ref={(node) => {
                  heroRingRefs.current[3] = node;
                }}
                className="absolute top-[-600px] w-[850px] h-[850px] md:w-[1700px] md:h-[1700px] border-2 border-white/15 md:border-white/45 rounded-full opacity-70"
              />
            </div>

            <div ref={heroContentRef} className="max-w-5xl mx-auto px-6 text-center relative z-10 pt-10 pb-20 flex-1 flex flex-col justify-center">
              <motion.div data-hero-badge style={{ opacity: 0 }}>
                <div className="inline-block px-5 py-2 rounded-full border border-red-800/80 mb-8 mx-auto">
                  <span className="text-[11px] font-bold tracking-[0.1em] text-[#FF4D4D] uppercase">
                    {t('hero.badge')}
                  </span>
                </div>
              </motion.div>

              <motion.div>
                <h1 className="text-[3.5rem] md:text-[5.5rem] font-bold text-white mb-6 tracking-tighter leading-none">
                  <span data-hero-title className="opacity-0 inline" />
                  <span data-hero-cursor className="opacity-0 inline-block ml-1 text-white/40" style={{ lineHeight: 1 }}>|</span>
                </h1>
              </motion.div>

              <motion.div data-hero-subtitle style={{ opacity: 0 }}>
                <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto mb-10 leading-relaxed font-light">
                  {t('hero.subtitle')}
                </p>
              </motion.div>

              <motion.div data-hero-cta style={{ opacity: 0 }}>
                <div className="flex flex-col items-center gap-4 mb-20">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <button
                      onClick={handleGetStarted}
                      className="w-full sm:w-auto px-8 py-4 bg-[#A30000] hover:bg-red-700 text-white rounded-xl font-bold transition-all shadow-[0_0_30px_-5px_rgba(163,0,0,0.6)] text-[15px]"
                    >
                      {t('hero.startBtn')}
                    </button>
                    <button
                      onClick={handleOpenVideoModal}
                      className="w-full sm:w-auto px-8 py-4 bg-[#1A1A1A] hover:bg-[#252525] text-white rounded-xl font-medium flex items-center justify-center gap-3 transition-colors border border-white/5 text-[15px]"
                    >
                      <Play className="w-5 h-5" /> {t('hero.demoBtn')}
                    </button>
                  </div>
                  {/* <button
                    onClick={() => setIsApiSyncModalOpen(true)}
                    id="hero-api-key-btn"
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-[13px] font-semibold text-neutral-400 hover:text-white transition-all border border-white/10 hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.06]"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    Input API Key
                  </button> */}
                </div>
              </motion.div>
            </div>

            {/* Stats Bar */}
            <div ref={statsBarRef} className="w-full bg-[#181212] border-t border-[#2A1A1A] py-8 relative z-10">
              <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row items-center justify-between gap-8">
                <div className="flex flex-wrap items-center justify-center gap-4 md:gap-10 min-w-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[#FF3B3B] font-bold text-xl md:text-2xl lg:text-3xl mb-1 tracking-tight">
                      <Users className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" /> <span ref={tradersNumberRef} style={{ opacity: 0 }}>0+</span>
                    </div>
                    <div className="text-neutral-500 text-[10px] md:text-xs font-semibold tracking-widest uppercase">
                      {t('stats.activeTraders')}
                    </div>
                  </div>
                  <div className="hidden md:block w-px h-10 bg-white/10" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[#FF3B3B] font-bold text-xl md:text-2xl lg:text-3xl mb-1 tracking-tight">
                      <BarChart2 className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" /> <span ref={volumeNumberRef} style={{ opacity: 0 }}>0+</span>
                    </div>
                    <div className="text-neutral-500 text-[10px] md:text-xs font-semibold tracking-widest uppercase">
                      {t('stats.volumeTracked')}
                    </div>
                  </div>
                  <div className="hidden md:block w-px h-10 bg-white/10" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-[#FF3B3B] font-bold text-xl md:text-2xl lg:text-3xl mb-1 tracking-tight">
                      <Wallet className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" /> Rp 0
                    </div>
                    <div className="text-neutral-500 text-[10px] md:text-xs font-semibold tracking-widest uppercase">
                      {t('stats.toStart')}
                    </div>
                  </div>
                </div>

                <div className="hidden lg:block w-px h-10 bg-white/10" />

                <div className="w-full lg:w-auto flex flex-col lg:flex-row items-center justify-center lg:justify-end gap-4 lg:gap-8 lg:flex-1">
                  <div className="flex flex-col items-center lg:items-end text-center lg:text-right">
                    <div className="text-neutral-500 text-[10px] font-semibold tracking-widest uppercase mb-1">
                      {t('stats.trustedBy')}
                    </div>
                    <div className="flex items-center gap-2 text-white font-bold text-lg">
                      <MapPin className="w-5 h-5 text-neutral-400" /> {t('stats.across')}
                    </div>
                  </div>

                  <div className="w-full lg:w-auto flex justify-center lg:justify-end">
                    <div className="bg-[#120B0B] border border-white/5 rounded-[30px] p-2 pr-6 flex items-center gap-4 flex-shrink-0">
                      <div className="flex -space-x-3">
                        <div className="w-10 h-10 rounded-full bg-neutral-800 border-2 border-[#120B0B] overflow-hidden">
                          <img src="https://i.pravatar.cc/100?img=11" alt="user" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-neutral-800 border-2 border-[#120B0B] overflow-hidden">
                          <img src="https://i.pravatar.cc/100?img=12" alt="user" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-neutral-800 border-2 border-[#120B0B] overflow-hidden">
                          <img src="https://i.pravatar.cc/100?img=13" alt="user" />
                        </div>
                        <div className="w-10 h-10 rounded-full bg-[#FF3B3B] border-2 border-[#120B0B] flex items-center justify-center text-white text-xs font-bold">
                          +2k
                        </div>
                      </div>
                      <div>
                        <div className="text-white text-xs font-bold mb-0.5">
                          {t('stats.join')}
                        </div>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                              key={i}
                              className="w-3 h-3 fill-[#FACC15] text-[#FACC15]"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div ref={featuresSectionRef} className="relative h-[400vh]">
            <div className="sticky top-0 h-screen flex items-center bg-[#050505]">
              <div className="max-w-6xl mx-auto px-6 w-full grid md:grid-cols-2 gap-16 items-center">
                <div className="flex flex-col divide-y divide-white/5">
                  {panels.map((panel, index) => (
                    <div key={panel.id} className="py-8">
                      <div className="flex items-center gap-8">
                        <span className={`text-[11px] font-bold tracking-widest transition-colors duration-500 ${activeFeature === index ? 'text-[#E26A59]' : 'text-neutral-700'}`}>
                          {panel.number}
                        </span>
                        <div className="flex-1">
                          <h3 className={`text-xl md:text-2xl font-bold tracking-tight transition-colors duration-500 mb-0 ${activeFeature === index ? 'text-white' : 'text-neutral-600'}`}>
                            {panel.title}
                          </h3>
                          <motion.div
                            initial={false}
                            animate={{ height: activeFeature === index ? 'auto' : 0, opacity: activeFeature === index ? 1 : 0 }}
                            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                            style={{ overflow: 'hidden' }}
                          >
                            <p className="text-neutral-500 text-sm leading-relaxed pt-3 max-w-sm">
                              {panel.description}
                            </p>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="hidden md:flex items-center justify-center h-[400px] relative">
                  {panels.map((panel, index) => (
                    <motion.div
                      key={panel.id}
                      className="absolute inset-0 flex items-center justify-center"
                      initial={false}
                      animate={{
                        opacity: activeFeature === index ? 1 : 0,
                        scale: activeFeature === index ? 1 : 0.92,
                        y: activeFeature === index ? 0 : 24,
                      }}
                      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                    >
                      {panel.visual === 'chart' && (
                        <div className="w-full bg-[#1C0808] rounded-[32px] p-8">
                          <div className="flex justify-between items-center mb-6">
                            <div className="text-[10px] font-bold text-[#BDA29F] tracking-[0.1em] uppercase">{t('bento.weekly')}</div>
                            <div className="text-sm font-bold text-white">+12.4%</div>
                          </div>
                          <div className="flex items-end gap-2.5 h-[120px] w-full">
                            <div className="flex-1 h-[20%] bg-[#6E332A] rounded-t-[4px]" />
                            <div className="flex-1 h-[10%] bg-[#6E332A] rounded-t-[4px]" />
                            <div className="flex-1 h-[40%] bg-[#A8726A] rounded-t-[4px]" />
                            <div className="flex-1 h-[15%] bg-[#6E332A] rounded-t-[4px]" />
                            <div className="flex-1 h-[100%] bg-[#FFB3A6] rounded-t-[4px]" />
                            <div className="flex-1 h-[30%] bg-[#A8726A] rounded-t-[4px]" />
                          </div>
                        </div>
                      )}
                      {panel.visual === 'network' && (
                        <div className="w-full bg-[#231514] rounded-[32px] p-12 flex items-center justify-center">
                          <Network className="w-32 h-32 text-[#E26A59]" />
                        </div>
                      )}
                      {panel.visual === 'zap' && (
                        <div className="w-full bg-gradient-to-b from-[#0C1F15] to-[#050D08] rounded-[32px] p-12 flex items-center justify-center">
                          <Zap className="w-32 h-32 text-[#34D399] fill-[#34D399]" />
                        </div>
                      )}
                      {panel.visual === 'shield' && (
                        <div className="w-full bg-[#330B08] rounded-[32px] p-12 flex flex-col items-center justify-center gap-6">
                          <img src={shieldLandingAsset} alt="" className="w-40 h-auto" />
                          <div className="grid grid-cols-2 gap-3 w-full">
                            {[t('bento.points.readonly'), t('bento.points.nocustody'), t('bento.points.secure'), t('bento.points.privacy')].map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-white">
                                <CheckCircle2 className="w-4 h-4 text-[#E26A59] flex-shrink-0" />
                                {item}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* COMPARISON - THE COST OF BLIND TRADING */}
          <section ref={compSectionRef} className="relative flex flex-col w-full min-h-[800px] overflow-hidden bg-[#050505]">
            {/* Split Backgrounds */}
            <div className="absolute inset-0 flex flex-col md:flex-row w-full h-full">
              <div ref={compLeftBgRef} className="w-full h-1/2 md:w-1/2 md:h-full bg-[#1A0605] border-b md:border-b-0 md:border-r border-[#2A0D0B]" />
              <div ref={compRightBgRef} className="w-full h-1/2 md:w-1/2 md:h-full bg-[#0A1A12] border-t md:border-t-0 md:border-l border-[#123322]" />
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col items-center w-full h-full pt-24 pb-32">
              <div data-comp-badge className="text-[10px] font-bold tracking-widest text-[#E26A59] uppercase mb-4 text-center">
                {t('comparison.badge')}
              </div>
              <h2 data-comp-title className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight text-center">
                {t('comparison.title')}
              </h2>
              <p data-comp-subtitle className="text-[#8C8C8C] text-sm md:text-base text-center mb-16 md:mb-24 px-6">
                {t('comparison.subtitle')}
              </p>

              <div className="flex flex-col md:flex-row w-full max-w-5xl mx-auto px-6 gap-16 md:gap-0">
                {/* Left Box (Red) */}
                <div data-comp-left className="w-full md:w-1/2 flex flex-col items-center text-center px-4 md:pr-12 md:pl-0">
                  <div className="inline-block px-5 py-1.5 rounded-full border border-[#4A1410] text-[#E26A59] text-[10px] font-bold tracking-widest uppercase mb-12">
                    {t('comparison.without')}
                  </div>

                  <div className="mb-16">
                    <div className="text-[11px] font-bold text-[#A37B75] uppercase tracking-wider mb-4">
                      {t('comparison.tradeAccuracy')}
                    </div>
                    <div className="text-[80px] md:text-[140px] leading-none font-bold text-[#C7483B] mb-4 tracking-tighter">
                      <span ref={comp42Ref}>0%</span>
                    </div>
                    <div className="text-[10px] font-bold tracking-widest text-[#A37B75] uppercase">
                      {t('comparison.inconsistent')}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] font-bold text-[#A37B75] uppercase tracking-wider mb-4">
                      {t('comparison.emotionalImpact')}
                    </div>
                    <div className="text-[60px] md:text-[110px] leading-none font-bold text-[#C7483B] mb-4 tracking-tighter">
                      {t('comparison.high')}
                    </div>
                    <div className="text-[10px] font-bold tracking-widest text-[#A37B75] uppercase">
                      {t('comparison.reactionary')}
                    </div>
                  </div>
                </div>

                {/* Right Box (Green) */}
                <div data-comp-right className="w-full md:w-1/2 flex flex-col items-center text-center px-4 md:pl-12 md:pr-0">
                  <div className="inline-block px-5 py-1.5 rounded-full border border-[#163322] text-[#34D399] text-[10px] font-bold tracking-widest uppercase mb-12 mt-16 md:mt-0">
                    {t('comparison.with')}
                  </div>

                  <div className="mb-16">
                    <div className="text-[11px] font-bold text-[#34D399] uppercase tracking-wider mb-4">
                      {t('comparison.tradeAccuracy')}
                    </div>
                    <div className="text-[80px] md:text-[140px] leading-none font-bold text-[#34D399] mb-4 tracking-tighter">
                      <span ref={comp89Ref}>0%</span>
                    </div>
                    <div className="text-[10px] font-bold tracking-widest text-[#34D399] uppercase">
                      {t('comparison.datadriven')}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] font-bold text-[#34D399] uppercase tracking-wider mb-4">
                      {t('comparison.emotionalImpact')}
                    </div>
                    <div className="text-[60px] md:text-[110px] leading-none font-bold text-[#34D399] mb-4 tracking-tighter">
                      {t('comparison.low')}
                    </div>
                    <div className="text-[10px] font-bold tracking-widest text-[#34D399] uppercase">
                      {t('comparison.systematic')}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-center w-full mt-16 relative z-20 px-6">
                <button
                  data-comp-cta
                  onClick={handleGetStarted}
                  className="px-8 py-4 bg-[#FF6B55] hover:bg-[#ff7a66] text-[#240C0A] rounded-[16px] font-bold transition-all flex items-center justify-center gap-2 shadow-[0_10px_30px_-5px_rgba(255,107,85,0.4)]"
                >
                  {t('comparison.bridge')} <ArrowRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </section>

          <section ref={mockupSectionRef} className="py-24 overflow-hidden">
            <div className="max-w-3xl mx-auto px-6 text-center mb-12 md:mb-16">
              <div data-mockup-copy className="text-xs font-semibold tracking-widest text-red-500 uppercase mb-4 opacity-0">
                {t('appMockup.badge')}
              </div>
              <h2 data-mockup-copy className="text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight whitespace-pre-wrap opacity-0">
                {t('appMockup.title')}
              </h2>
              <p data-mockup-copy className="text-base md:text-lg text-neutral-400 opacity-0">
                {t('appMockup.desc')}
              </p>
            </div>

            <div data-mockup-tabs className="flex justify-center px-4 mb-10 md:mb-12 opacity-0">
              <div className="flex max-w-full overflow-x-auto bg-[#101014] p-1 rounded-full border border-white/5">
                {mockupTabs.map((tab, idx) => {
                  const isActive = activeTab === idx;
                  const isDisabled = false;
                  return (
                    <button
                      key={tab.slug}
                      type="button"
                      onClick={() => setActiveTab(idx)}
                      className={`relative flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 rounded-full text-sm font-medium whitespace-nowrap ${isActive ? "text-white" : "text-neutral-400 hover:text-white"}`}
                      aria-pressed={isActive}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="activePill"
                          className="absolute inset-0 rounded-full bg-red-600 shadow-lg"
                          transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
                        />
                      )}
                      <motion.span
                        className="relative z-10 flex items-center justify-center"
                        animate={isActive ? { scale: [1, 1.12, 1] } : { scale: 1 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <tab.icon className="w-4 h-4" />
                      </motion.span>
                      <span className="relative z-10 hidden sm:inline">{tab.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div data-mockup-frame className="relative opacity-0 overflow-hidden">
                <motion.div
                  className="absolute left-1/2 bottom-0 h-24 w-[78%] -translate-x-1/2 rounded-full bg-red-600 blur-3xl pointer-events-none"
                  animate={{ opacity: [0.15, 0.35, 0.15] }}
                  transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="relative rounded-[22px] md:rounded-[30px] bg-[#050506] p-2 md:p-3 shadow-[0_28px_90px_rgba(0,0,0,0.48)]">
                  <div className="aspect-[16/10] rounded-[16px] md:rounded-[24px] bg-[#0A0A0C] border border-[#24242C] overflow-hidden flex flex-col">
                    <div className="h-9 md:h-11 border-b border-[#24242C] flex items-center gap-3 px-3 md:px-4 bg-[#09090B]">
                      <div className="flex gap-1.5 md:gap-2">
                        <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#FF5F57]" />
                        <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#FFBD2E]" />
                        <span className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-[#28C840]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="mx-auto max-w-[210px] sm:max-w-sm md:max-w-lg rounded-md bg-[#15151A] border border-white/5 px-3 py-1 text-center text-[10px] md:text-xs font-mono text-neutral-500 truncate">
                          catatcrypto.com/{activeMockupTab.slug}
                        </div>
                      </div>
                      <div className="w-10 md:w-14" />
                    </div>
                    <div ref={frameContentRef} className="relative flex-1 bg-[#050505] overflow-hidden">
                      <div className="relative w-full h-full overflow-hidden bg-[#050505]">
                        {mockupTabs.map((tab, idx) => {
                          const isActive = activeTab === idx;
                          return (
                            <motion.img
                              key={tab.slug}
                              ref={(el) => {
                                imgRefs.current[idx] = el;
                              }}
                              src={tab.asset ?? ''}
                              alt={tab.name}
                              className={`${tab.needsScroll ? "absolute top-0 left-0 w-full h-auto" : "absolute inset-0 w-full h-full object-cover object-top"} ${isActive ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                              animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 1.02 }}
                              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                            />
                          );
                        })}
                      </div>
                      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />
                    </div>
                  </div>
                </div>
                <div className="relative mx-auto h-3 w-[88%] rounded-b-[26px] bg-[#101014] border-x border-b border-[#24242C]" />
              </div>
            </div>
          </section>

          {/* CATAT CRYPTO HUB - BROKERS */}
          <section className="py-32 bg-[#050505] relative overflow-hidden">
            <div className="max-w-4xl mx-auto px-6 text-center mb-16 relative z-10">
              <div className="text-[10px] font-bold tracking-widest text-[#E26A59] uppercase mb-4">
                {t('hub.badge')}
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {t('hub.title')}
              </h2>
              <p className="text-[#8C8C8C] text-sm md:text-base whitespace-pre-wrap">
                {t('hub.desc')}
              </p>
            </div>

            <div ref={brokerStageRef} className="relative w-full h-[500px] md:h-[600px] flex items-center justify-center">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#4a0404] blur-[150px] opacity-30 pointer-events-none" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                <motion.div
                  className="absolute inset-0 rounded-full bg-[#4a0404] blur-2xl opacity-50"
                  animate={{ scale: [1, 1.03, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                />
                <div className="w-20 h-20 md:w-32 md:h-32 bg-[#1A0605] border border-[#3A1410] rounded-full flex items-center justify-center p-4 md:p-6 shadow-[0_0_50px_rgba(227,47,47,0.2)]">
                  <img 
                    src={logoAsset} 
                    alt="Catat Crypto" 
                    className="w-full h-full object-contain"
                    style={{
                      filter: document.documentElement.classList.contains('light') ? 'brightness(0)' : 'none'
                    }}
                  />
                </div>
              </div>
              {/* Single unified orbit - Desktop */}
              <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block cursor-pointer z-10" width="660" height="232" viewBox="-330 -116 660 232" onClick={handleOrbitToggle} onMouseEnter={() => setOrbitGlowActive(true)} onMouseLeave={() => setOrbitGlowActive(false)}>
                <ellipse cx="0" cy="0" rx="320" ry="106" stroke={orbitGlowActive ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.15)'} strokeWidth="1" fill="none" style={{ filter: orbitGlowActive ? 'drop-shadow(0 0 8px rgba(255,255,255,0.7))' : 'none', transition: 'all 0.3s ease' }} />
              </svg>
              {/* Single unified orbit - Mobile */}
              <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 block md:hidden cursor-pointer z-10" viewBox="-180 -73 360 146" style={{ width: '90vw', maxWidth: '360px', height: 'auto' }} onClick={handleOrbitToggle} onMouseEnter={() => setOrbitGlowActive(true)} onMouseLeave={() => setOrbitGlowActive(false)}>
                <ellipse cx="0" cy="0" rx="170" ry="63" stroke={orbitGlowActive ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.15)'} strokeWidth="1" fill="none" style={{ filter: orbitGlowActive ? 'drop-shadow(0 0 8px rgba(255,255,255,0.7))' : 'none', transition: 'all 0.3s ease' }} />
              </svg>
              {/* CEX Brokers */}
              {brokerKeys.map((broker) => (
                <div
                  key={broker}
                  ref={brokerRefs[broker]}
                  className="broker-trigger absolute top-1/2 left-1/2 flex flex-col items-center gap-1 cursor-pointer"
                  style={{ zIndex: 8, filter: orbitGlowActive ? `drop-shadow(0 0 10px ${brokerInfo[broker].glow})` : 'none', transition: 'filter 0.3s ease' }}
                  onMouseEnter={() => setHoveredBroker(broker)}
                  onMouseLeave={() => setHoveredBroker(null)}
                  onClick={() => handleBrokerClick(broker)}
                >
                  <div className={`w-11 h-11 md:w-14 md:h-14 bg-white border rounded-full flex items-center justify-center p-2 md:p-2.5 transition-all duration-200 shadow-lg ${hoveredBroker === broker ? 'border-white/30 scale-110' : 'border-white/10'}`}
                    style={{ filter: hoveredBroker === broker ? `drop-shadow(0 0 8px ${brokerInfo[broker].glow})` : 'none' }}
                  >
                    <img src={brokerConfigs[broker].icon} alt={brokerConfigs[broker].alt} className="w-full h-full object-contain" />
                  </div>
                  <span className="text-[8px] md:text-[9px] uppercase font-semibold text-neutral-500">{brokerInfo[broker].name}</span>
                  {renderBrokerPopup(broker)}
                </div>
              ))}
              {/* Wallets & DEX */}
              {walletKeys.map((wallet) => (
                <div
                  key={wallet}
                  ref={walletRefs[wallet]}
                  className="wallet-trigger absolute top-1/2 left-1/2 flex flex-col items-center gap-1 cursor-pointer"
                  style={{ zIndex: 7, filter: orbitGlowActive ? `drop-shadow(0 0 8px ${walletInfo[wallet].glow})` : 'none', transition: 'filter 0.3s ease' }}
                  onMouseEnter={() => setHoveredWallet(wallet)}
                  onMouseLeave={() => setHoveredWallet(null)}
                  onClick={() => handleWalletClick(wallet)}
                >
                  <div className={`w-11 h-11 md:w-14 md:h-14 bg-white border rounded-full flex items-center justify-center p-2 md:p-2.5 transition-all duration-200 shadow-lg ${hoveredWallet === wallet ? 'border-purple-400/40 scale-110' : 'border-white/10'}`}
                    style={{ filter: hoveredWallet === wallet ? `drop-shadow(0 0 6px ${walletInfo[wallet].glow})` : 'none' }}
                  >
                    <img src={walletConfigs[wallet].icon} alt={walletConfigs[wallet].alt} className="w-full h-full object-contain" />
                  </div>
                  <span className="text-[8px] md:text-[9px] uppercase font-semibold text-neutral-600">{walletInfo[wallet].name}</span>
                  {renderWalletPopup(wallet)}
                </div>
              ))}
            </div>

            <div className="text-center mt-12 text-[#8C8C8C] text-sm relative z-10">
              {t('hub.missingUrl')}
            </div>
          </section>

          {/* PRICING */}
          <section className="py-24 bg-[#0A0A0C]" data-pricing-section>
            <div className="max-w-4xl mx-auto px-6 text-center mb-10">
              <motion.div
                initial={{ opacity: 0, y: 48 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: 0.1 }}
              >
                <h2 className="text-4xl font-bold text-white mb-6">
                  {t('pricing.title')}
                </h2>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 48 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: 0.3 }}
              >
                <p className="text-lg text-neutral-400">
                  {t('pricing.desc')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1], delay: 0.45 }}
                className="flex items-center justify-center gap-3 mt-8"
              >
                <span className={`text-sm font-bold transition-colors ${pricingCycle === 'monthly' ? 'text-white' : 'text-neutral-500'}`}>
                  {t('pricing.monthly')}
                </span>
                <button
                  onClick={() => setPricingCycle(c => c === 'monthly' ? 'yearly' : 'monthly')}
                  className={`relative w-12 h-6 rounded-full transition-colors ${pricingCycle === 'yearly' ? 'bg-red-600' : 'bg-neutral-700'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${pricingCycle === 'yearly' ? 'left-7' : 'left-1'}`} />
                </button>
                <span className={`text-sm font-bold transition-colors ${pricingCycle === 'yearly' ? 'text-white' : 'text-neutral-500'}`}>
                  {t('pricing.yearly')}
                </span>
              </motion.div>
            </div>

            <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
              {[
                {
                  id: 'free',
                  name: t('pricing.free'),
                  monthlyPrice: 0,
                  yearlyPrice: 0,
                  discount: 0,
                  subtitle: t('pricing.freeDesc'),
                  Icon: Star,
                  color: 'neutral' as const,
                  isFree: true,
                  features: [t('pricing.freeLimit')],
                  cta: t('pricing.freeBtn'),
                  delay: 0.2,
                },
                {
                  id: 'pro',
                  name: t('pricing.pro'),
                  monthlyPrice: 99000,
                  yearlyPrice: 949000,
                  discount: 20,
                  subtitle: t('pricing.proDesc'),
                  Icon: Zap,
                  color: 'red' as const,
                  popular: true,
                  features: [
                    t('pricing.features.auto'),
                    t('pricing.features.multi'),
                    t('pricing.features.cal'),
                    t('pricing.features.port'),
                    t('pricing.features.add'),
                    t('pricing.features.aiAssistant'),
                  ],
                  cta: t('pricing.proBtn'),
                  delay: 0.35,
                },
                {
                  id: 'premium',
                  name: t('pricing.premium.title'),
                  monthlyPrice: 179000,
                  yearlyPrice: 1499000,
                  discount: 22,
                  subtitle: t('pricing.premiumDesc'),
                  Icon: Crown,
                  color: 'purple' as const,
                  features: [
                    t('pricing.premium.f1'),
                    t('pricing.premium.f2'),
                    t('pricing.premium.f3'),
                    t('pricing.premium.f4'),
                    t('pricing.premium.f5'),
                  ],
                  cta: t('pricing.premiumBtn'),
                  delay: 0.5,
                },
              ].map((plan) => {
                const price = pricingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
                const originalYearly = plan.monthlyPrice * 12;
                const showYearlyDiscount = pricingCycle === 'yearly' && !plan.isFree;

                const colorMap = {
                  neutral: {
                    ring: '',
                    shadow: '',
                    badge: 'bg-neutral-800 text-neutral-400',
                    icon: 'bg-neutral-800',
                    check: 'text-neutral-500',
                    checkBg: 'bg-neutral-700/50 border-neutral-700',
                    btn: 'bg-neutral-800 hover:bg-neutral-700 text-white',
                    saveBg: '',
                  },
                  red: {
                    ring: 'ring-2 ring-red-600/60',
                    shadow: 'shadow-2xl shadow-red-900/20',
                    badge: 'bg-red-600 text-white',
                    icon: 'bg-red-600',
                    check: 'text-red-500',
                    checkBg: 'bg-red-600/20 border-red-600/30',
                    btn: 'bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-900/40',
                    saveBg: 'bg-red-600/20 border-red-500/30 text-red-300',
                  },
                  purple: {
                    ring: 'ring-2 ring-purple-600/60',
                    shadow: '',
                    badge: 'bg-purple-600 text-white',
                    icon: 'bg-purple-600',
                    check: 'text-purple-500',
                    checkBg: 'bg-purple-600/20 border-purple-600/30',
                    btn: 'bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-900/40',
                    saveBg: 'bg-purple-600/20 border-purple-500/30 text-purple-300',
                  },
                } as const;

                const colors = colorMap[plan.color];

                return (
                  <motion.div
                    key={plan.id}
                    className={`relative bg-[#0D0D0D] border border-neutral-800 rounded-3xl p-6 flex flex-col w-full max-w-md mx-auto lg:max-w-none ${colors.ring} ${colors.shadow} ${plan.color === 'red' ? 'lg:-translate-y-4' : ''}`}
                    initial={{ opacity: 0, y: 56 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    whileHover={{
                      y: -8,
                      boxShadow: "0 18px 55px -12px rgba(220,38,38,0.35)",
                      transition: { duration: 0.3, ease: "easeOut" },
                    }}
                    viewport={{ once: false, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: plan.delay }}
                  >
                    {plan.popular && (
                      <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${colors.badge}`}>
                        {t('pricing.proBadge')}
                      </div>
                    )}

                    <div className="mb-6">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-4 ${colors.icon}`}>
                        <plan.Icon size={20} className="text-white" />
                      </div>
                      <h3 className="text-xl font-black text-white">{plan.name}</h3>
                      <p className="text-xs text-neutral-500 mt-0.5">{plan.subtitle}</p>
                    </div>

                    <div className="mb-6">
                      {showYearlyDiscount && (
                        <div className="mb-2 flex items-center gap-2">
                          <span className="text-sm font-bold text-neutral-500 line-through decoration-red-500/80 decoration-2">
                            IDR {originalYearly.toLocaleString('en-US')}
                          </span>
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${colors.saveBg}`}>
                            {t('pricing.save')} {plan.discount}%
                          </span>
                        </div>
                      )}
                      <div className="flex items-end gap-1.5">
                        <span className="text-3xl font-black text-white">
                          {plan.isFree ? 'Free' : `IDR ${price.toLocaleString('en-US')}`}
                        </span>
                        {!plan.isFree && (
                          <span className="pb-1 text-sm text-neutral-500">{pricingCycle === 'yearly' ? t('pricing.perYear') : t('pricing.perMonth')}</span>
                        )}
                      </div>
                      {pricingCycle === 'yearly' && !plan.isFree && (
                        <p className="text-[10px] text-neutral-500 mt-1">
                          {isId ? `Setara IDR ${Math.round(price / 12).toLocaleString('en-US')}${t('pricing.perMonth')}` : `Equivalent to IDR ${Math.round(price / 12).toLocaleString('en-US')}${t('pricing.perMonth')}`}
                        </p>
                      )}
                    </div>

                    <div className="space-y-3 flex-1 mb-8">
                      {plan.features.map((f) => (
                        <div key={f} className="flex items-start gap-3">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 border ${colors.checkBg}`}>
                            <Check size={9} className={colors.check} />
                          </div>
                          <span className="text-sm text-neutral-300 leading-tight">{f}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={handleGetStarted}
                      className={`w-full py-3.5 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all ${
                        plan.isFree
                          ? 'bg-neutral-800 text-neutral-500 cursor-default'
                          : colors.btn
                      }`}
                    >
                      {plan.isFree ? plan.cta : <>{plan.cta} <ArrowRight size={16} /></>}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </section>

          <section ref={testimonialsSectionRef} className="py-24 bg-[#0D0D0D] overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
              {(() => {
                const reviews = [
                  {
                    name: "Rizky A.",
                    role: "DAY TRADER, BYBIT",
                    quote:
                      "Sejak pakai Catat Crypto, aku bisa lihat pola trading aku jauh lebih jelas. Win rate aku naik 15% dalam sebulan pertama.",
                    platform: "INDODAX",
                    verified: true,
                  },
                  {
                    name: "Budi Santoso",
                    role: "DAY TRADER, TOKOCRYPTO",
                    quote:
                      "Catat Crypto adalah investasi terbaik tahun ini. Fitur analitiknya benar-benar membuka mata tentang risiko manajemen yang saya remehkan selama 2 tahun terakhir.",
                    platform: "TOKOCRYPTO",
                    verified: true,
                  },
                  {
                    name: "Dian Anggraini",
                    role: "ACTIVE TRADER, BINANCE",
                    quote:
                      "UI-nya sangat intuitif. Gak perlu pusing lagi rekap manual di Excel. Sangat membantu untuk evaluasi psikologi trading yang selama ini terabaikan.",
                    platform: "BINANCE",
                    verified: true,
                  },
                  {
                    name: "Yuda P.",
                    role: "ADVANCE TRADER, JAKARTA",
                    quote:
                      "Dashboardnya sangat clean. Membantu saya tetap fokus pada data, bukan emosi.",
                    platform: "JAKARTA",
                    verified: true,
                  },
                  {
                    name: "Kevin M.",
                    role: "DAY TRADER, INDODAX PRO",
                    quote: "Sangat krusial untuk manajemen resiko.",
                    platform: "INDODAX",
                    verified: true,
                  },
                  {
                    name: "Adi S.",
                    role: "BASIC TRADER",
                    quote:
                      "Gak ada kata terlambat buat mulai journaling. Ini game changer buat portfolio management saya.",
                    platform: "BASIC",
                    verified: true,
                  },
                ];
                const columns = [
                  [reviews[0], reviews[2], reviews[4]],
                  [reviews[1], reviews[3], reviews[5]],
                ];
                
                // Theme detection
                const isDarkMode = !document.documentElement.classList.contains('light');
                
                const avatarColors = isDarkMode ? [
                  "bg-red-950 text-red-400",
                  "bg-emerald-950 text-emerald-400",
                  "bg-neutral-800 text-neutral-300",
                  "bg-neutral-700 text-neutral-300",
                  "bg-slate-700 text-slate-200",
                  "bg-emerald-950 text-emerald-400",
                ] : [
                  "bg-red-100 text-red-600",
                  "bg-emerald-100 text-emerald-600",
                  "bg-gray-200 text-gray-700",
                  "bg-gray-300 text-gray-700",
                  "bg-slate-200 text-slate-700",
                  "bg-emerald-100 text-emerald-600",
                ];

                return (
                  <>
                    <div className="grid lg:grid-cols-[minmax(220px,25%)_1fr] gap-12 lg:gap-16">
                      <div className="pt-1">
                        <div data-test-badge className="text-[10px] font-bold tracking-[0.28em] text-red-600 uppercase mb-8">
                          Wall of Social Proof
                        </div>
                        <h2 data-test-title className={`text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight mb-5 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                          <span className="inline-block overflow-hidden">
                            <span data-test-title-word className="inline-block" style={{ transform: 'translateY(100%)', minWidth: '1ch', display: 'inline-block' }}>Real</span>
                          </span>{" "}
                          <span className="inline-block overflow-hidden">
                            <span data-test-title-word className="inline-block" style={{ transform: 'translateY(100%)', minWidth: '1ch', display: 'inline-block' }}>Traders.</span>
                          </span>
                          <br />
                          <span className="inline-block overflow-hidden">
                            <span data-test-title-word className="inline-block" style={{ transform: 'translateY(100%)', minWidth: '1ch', display: 'inline-block' }}>Real</span>
                          </span>{" "}
                          <span className="inline-block overflow-hidden">
                            <span data-test-title-word className="inline-block" style={{ transform: 'translateY(100%)', minWidth: '1ch', display: 'inline-block' }}>Results.</span>
                          </span>
                        </h2>
                        <p data-test-desc className={`text-sm leading-relaxed max-w-[285px] mb-8 ${isDarkMode ? 'text-neutral-500' : 'text-gray-600'}`}>
                          Join thousands of professional Indonesian analysts who journal smarter and trade with confidence using Catat Crypto.
                        </p>
                        <div data-test-divider className={`h-px w-full max-w-[300px] mb-6 ${isDarkMode ? 'bg-white/5' : 'bg-gray-200'}`} />
                        <div data-test-join className={`flex items-center gap-4 text-xs ${isDarkMode ? 'text-neutral-500' : 'text-gray-600'}`}>
                          <div className="flex -space-x-2">
                            {["AJ", "MK", "SL"].map((initial, i) => (
                              <div
                                key={i}
                                className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                  isDarkMode 
                                    ? 'border-2 border-[#0D0D0D] bg-neutral-800 text-white' 
                                    : 'border-2 border-white bg-gray-200 text-black'
                                }`}
                              >
                                {initial}
                              </div>
                            ))}
                          </div>
                          <span>+12k joined this week</span>
                        </div>
                      </div>

                      <div ref={testimonialsGridRef} className="grid md:grid-cols-2 gap-6">
                        {columns.map((column, columnIndex) => (
                          <div key={columnIndex} className="flex flex-col gap-6">
                            {column.map((review, rowInColumn) => {
                              const reviewIndex = reviews.indexOf(review);
                              const hasStars = review.name === "Budi Santoso" || review.name === "Dian Anggraini";
                              const initials = review.name
                                .split(" ")
                                .map((part) => part[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase();

                              return (
                                <div
                                  key={review.name}
                                  className={`border rounded-xl p-7 min-h-[184px] flex flex-col justify-between transition-all ${
                                    isDarkMode
                                      ? 'bg-[#1A1A1A] border-white/5'
                                      : 'bg-white border-gray-200 shadow-sm'
                                  }`}
                                  data-test-card-left={columnIndex === 0 ? '' : undefined}
                                  data-test-card-right={columnIndex === 1 ? '' : undefined}
                                  data-test-card-index={String(columnIndex === 0 ? rowInColumn * 2 : rowInColumn * 2 + 1)}
                                >
                                  <div>
                                    <div className={`text-3xl leading-none font-black mb-4 ${isDarkMode ? 'text-red-900' : 'text-gray-300'}`}>”</div>
                                    <p className={`text-sm leading-relaxed italic ${isDarkMode ? 'text-neutral-200' : 'text-gray-700'}`}>
                                      "{review.quote}"
                                    </p>
                                    {hasStars && (
                                      <div className="flex items-center gap-0.5 mt-5">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <span
                                            key={star}
                                          >
                                            <Star
                                              className="w-3 h-3 fill-[#FACC15] text-[#FACC15]"
                                            />
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-6">
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-bold ${avatarColors[reviewIndex]}`}>
                                      {initials}
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>
                                          {review.name}
                                        </span>
                                        {review.verified && (
                                          <span className="inline-flex items-center gap-1 text-[8px] font-bold text-emerald-400 uppercase">
                                            <CheckCircle2 className="w-2.5 h-2.5" />
                                            Verified
                                          </span>
                                        )}
                                      </div>
                                      <div className={`text-[10px] font-medium uppercase tracking-wider ${isDarkMode ? 'text-neutral-500' : 'text-gray-500'}`}>
                                        {review.role}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div
                      className={`mt-14 pt-8 flex justify-end ${isDarkMode ? 'border-t border-white/5' : 'border-t border-gray-200'}`}
                    >
                      <div className={`flex flex-wrap items-center justify-end gap-8 ${isDarkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                        <span data-test-trust className="text-[10px] font-bold tracking-[0.28em] uppercase">
                          Trusted & Reviewed On
                        </span>
                        <span data-test-trust className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-black ${
                            isDarkMode ? 'bg-neutral-600 text-[#0D0D0D]' : 'bg-gray-300 text-white'
                          }`}>G</span>
                          Google
                        </span>
                        <span data-test-trust className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                          <CheckCircle2 className="w-5 h-5 fill-emerald-500 text-emerald-500" />
                          Trustpilot
                        </span>
                        <span data-test-trust className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                          <Zap className="w-5 h-5 fill-orange-500 text-orange-500" />
                          Product Hunt
                        </span>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </section>

          <section
            ref={partnerSectionRef}
            className="py-32 px-6 max-w-[1200px] mx-auto bg-[#050505]"
          >
            <div className="bg-[#3D0A0A] border border-[#521313] rounded-[32px] p-6 sm:p-10 lg:p-16 flex flex-col lg:flex-row items-center justify-between relative overflow-visible">
              <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_right_center,_var(--tw-gradient-stops))] from-red-500/10 via-transparent to-transparent opacity-50 mix-blend-screen pointer-events-none" />

              <div className="flex-1 relative z-10 w-full lg:w-auto mb-10 lg:mb-0">
                <div data-partner-badge className="flex items-center gap-1 mb-8">
                  <span data-partner-badge-logo className="inline-block">
                    <img src={logoAsset} alt="Catat Crypto" className="w-[74px] h-auto object-contain object-left" />
                  </span>
                  <div className="w-1 h-1 rounded-full bg-neutral-500 mx-1" />
                  <span data-partner-badge-word className="text-[10px] font-bold uppercase tracking-widest text-[#B38C8C]">
                    {t('partnership.badge')}
                  </span>
                </div>

                <h2 data-partner-title className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                  {t('partnership.title').split(' ').map((word, i) => (
                    <span key={i} data-partner-title-word className="inline-block mr-[0.25em]">{word}</span>
                  ))}
                  <span data-partner-title-word className="inline-block text-[#FF6B55]">{t('partnership.soon')}</span>
                </h2>

                <ul className="space-y-4 my-10">
                  {[
                    t('partnership.f1'),
                    t('partnership.f2'),
                    t('partnership.f3'),
                  ].map((item, i) => (
                    <li
                      key={i}
                      data-partner-features
                      className="flex items-center gap-3 text-[#E6CDCD] text-lg"
                    >
                      <CheckCircle2 className="w-5 h-5 text-[#FF6B55]" />
                      {item}
                    </li>
                  ))}
                </ul>

                <div data-partner-cta className="flex flex-col sm:flex-row items-center gap-6">
                  <button
                    onClick={() => navigate('/partnership')}
                    className="w-full sm:w-auto px-8 py-4 bg-[#FF6B55] hover:bg-[#ff7a66] text-[#240C0A] rounded-xl font-bold transition-all"
                  >
                    {t('partnership.btn')}
                  </button>
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-[#B38C8C] mb-1">
                      {t('partnership.payout')}
                    </span>
                    <span className="text-white font-bold text-xl">$1,450.00+</span>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-[44%] relative z-10 flex justify-center lg:justify-end overflow-visible lg:-mt-16 lg:pr-12 lg:pb-8 px-6 sm:px-8 lg:px-0">
                <div style={{ width: '100%', maxWidth: '360px', position: 'relative', paddingBottom: '3rem', isolation: 'isolate' }}>
                  <motion.div
                    data-partner-card
                    ref={partnerCardRef}
                    className="rounded-3xl overflow-hidden rotate-[-6deg] shadow-2xl will-change-transform w-full"
                  >
                    <img
                      src={contactAsset}
                      alt=""
                      className="block w-full h-auto rotate-[6deg] scale-110"
                    />
                  </motion.div>
                  <motion.div
                    data-partner-badge-float
                    ref={partnerBadgeRef}
                    className="absolute bottom-0 left-0 sm:-bottom-4 sm:-left-8 bg-white rounded-[14px] shadow-[0_14px_28px_rgba(0,0,0,0.22)] px-3 py-2 sm:px-4 sm:py-3 rotate-[-7deg] z-20 flex items-center gap-2 sm:gap-3 will-change-transform scale-90 sm:scale-100 origin-bottom-left"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'translateZ(0)',
                    }}
                  >
                    <div className="w-9 h-9 rounded-full bg-[#2A0707] flex items-center justify-center overflow-hidden flex-shrink-0">
                      <Bot className="w-5 h-5 text-[#FF6B55]" />
                    </div>
                    <div className="leading-none">
                      <div className="text-[9px] font-bold uppercase tracking-[0.08em] text-[#1A0A0A] mb-1.5">
                        Estimated Payout
                      </div>
                      <div className="text-xl font-black text-[#D60000] tracking-tight whitespace-nowrap">
                        IDR 18 MIO
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA SECTION */}
          <section ref={ctaSectionRef} className="py-24 px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h2 ref={ctaHeadlineRef} className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight whitespace-pre-wrap">
                <span data-cta-title1 />
                <span className="text-red-600" data-cta-title2 />
                <span data-cta-cursor className="inline-block ml-1 text-white" style={{ opacity: 0 }}>|</span>
              </h2>
              <p className="text-lg text-neutral-400 mb-12 max-w-2xl mx-auto">
                {t('cta.desc')}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <button
                  onClick={handleGetStarted}
                  className="w-full sm:w-auto px-10 py-5 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold text-lg transition-all shadow-[0_20px_40px_-10px_rgba(220,38,38,0.3)]"
                >
                  {t('cta.btn1')}
                </button>
                <button
                  onClick={() => handleFeatureClick(0)}
                  className="w-full sm:w-auto px-10 py-5 bg-white/5 hover:bg-white/10 text-white rounded-2xl font-bold text-lg transition-all border border-white/10 flex items-center justify-center gap-3"
                >
                  <Play className="w-5 h-5" /> {t('cta.btn2')}
                </button>
              </div>
              <div className="flex flex-wrap justify-center gap-8 opacity-50">
                {[t('cta.f1'), t('cta.f2'), t('cta.f3')].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-white">
                    <CheckCircle2 className="w-4 h-4 text-red-500" /> {f}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>
          ) : currentPage === 'about' ? (
            <AboutUs />
          ) : currentPage === 'contact' ? (
            <Contact />
          ) : currentPage === 'privacy' ? (
            <Privacy />
          ) : currentPage === 'terms' ? (
            <Terms />
          ) : null}
        </motion.div>
      </AnimatePresence>

      {/* VIDEO MODAL - Demo Video Popup */}
      <AnimatePresence>
        {isVideoModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleCloseVideoModal}
            className="fixed inset-0 z-[9999] flex items-center justify-center px-4 bg-black/80 backdrop-blur-sm"
            style={{ margin: 0 }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full max-w-5xl ${
                document.documentElement.classList.contains('light')
                  ? 'bg-white border-gray-200'
                  : 'bg-[#0a0a0a] border-[#E50914]/20'
              } border rounded-2xl shadow-2xl overflow-hidden`}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseVideoModal}
                className={`absolute top-4 right-4 z-10 p-2 rounded-full transition-all ${
                  document.documentElement.classList.contains('light')
                    ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                <X className="w-5 h-5" />
              </button>

              {/* Video Content */}
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                {DEMO_VIDEO_URL ? (
                  /* YouTube Video Embed */
                  <iframe
                    src={`${DEMO_VIDEO_URL}?autoplay=1&rel=0`}
                    title="CatatCrypto Demo Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute top-0 left-0 w-full h-full"
                  />
                ) : (
                  /* Coming Soon Message */
                  <div className={`absolute top-0 left-0 w-full h-full flex flex-col items-center justify-center ${
                    document.documentElement.classList.contains('light')
                      ? 'bg-gray-50 text-black'
                      : 'bg-[#0c0c0f] text-white'
                  }`}>
                    <div className="text-6xl mb-6">🎬</div>
                    <h3 className={`text-2xl font-bold mb-3 ${
                      document.documentElement.classList.contains('light')
                        ? 'text-black'
                        : 'text-white'
                    }`}>
                      Demo Video Coming Soon
                    </h3>
                    <p className={`text-base ${
                      document.documentElement.classList.contains('light')
                        ? 'text-gray-600'
                        : 'text-neutral-400'
                    }`}>
                      Video demo sedang dalam proses produksi.
                    </p>
                    <p className={`text-sm mt-2 ${
                      document.documentElement.classList.contains('light')
                        ? 'text-gray-500'
                        : 'text-neutral-500'
                    }`}>
                      Stay tuned!
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FOOTER */}
      <Footer setCurrentPage={handleSetPage} onFeatureClick={handleFeatureClick} />

      <ApiSyncModal isOpen={isApiSyncModalOpen} onClose={() => setIsApiSyncModalOpen(false)} />
    </div>
  );
}
